"use client";
import { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import { sendNotification } from '@/lib/messaging';
import { logActivity, ADMIN_IDS, ALL_USERS } from '@/lib/activityLog';
import {
  Clock, Building2, Home, MapPin, CalendarCheck,
  CheckCircle2, XCircle, Hourglass, Plus, Paperclip,
  CloudUpload, FileCheck, Eye, ChevronDown, ChevronLeft, ChevronRight, Lock,
  AlertCircle, Receipt, Banknote, ArrowUpRight, FileText, CheckSquare, Square, Printer,
  RefreshCw, Trash2, Flag, Check, Users, Phone, CreditCard, Building,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import {
  checkIn, checkOut, getTodayLog, getUserLogs,
  revertExpenseToPending,
  getAllPendingLogs, approveLog, rejectLog, resubmitLog,
  getAllLeaves, cancelLeaveRequest, updateLeaveRequest, cancelLog, adminUpdateLog,
  submitLeaveRequest, getUserLeaveRequests, getAllPendingLeave,
  submitExpenseClaim, getUserExpenseClaims,
  approveExpense, rejectExpense, markExpenseTransferred, bulkMarkExpenseTransferred, updateExpenseReceiptStatus, updateExpenseClaim, deleteExpenseClaim, uploadExpenseReceipt, clearExpenseReceipt,
  createPaymentVoucher, getVouchersByStaff, getAllVouchers, signVoucher, setVoucherStatus, deletePaymentVoucher,
  submitRetroLog, updateLogAllowances,
  getUserProfile, saveUserProfile, getAllUserProfiles, updateTalentProfile, getNextVoucherNo,
  toDateStr, formatDate, formatTimestamp,
} from '@/lib/attendance';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { computeLeaveQuota, usedInCycle, DEFAULT_PROBATION_MONTHS } from '@/lib/leaveQuota';

// ── colour tokens ────────────────────────────────────────────────────────────
const LOC_COLOR = {
  office: { bg: '#e0d5f8', text: '#9b82d4', label: 'Office' },
  wfh:    { bg: '#cce5fb', text: '#6aabdf', label: 'WFH' },
  onsite: { bg: '#e2fbe5', text: '#5dbc78', label: 'On-site' },
};
const STATUS_BADGE = {
  pending:     { bg: '#fefce8', text: '#a16207', icon: <Hourglass size={10}/>,     label: 'รออนุมัติ' },
  approved:    { bg: '#eff6ff', text: '#1d4ed8', icon: <CheckCircle2 size={10}/>,  label: 'อนุมัติ · รอโอน' },
  transferred: { bg: '#f0fdf4', text: '#16a34a', icon: <Banknote size={10}/>,      label: 'โอนคืนแล้ว' },
  rejected:    { bg: '#fff1f2', text: '#dc2626', icon: <XCircle size={10}/>,       label: 'ไม่อนุมัติ' },
};

// ── month helpers ────────────────────────────────────────────────────────────
const TH_MONTHS = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.'];
function prevMonth(ym) { const [y,m]=ym.split('-').map(Number); return m===1?`${y-1}-12`:`${y}-${String(m-1).padStart(2,'0')}`; }
function nextMonth(ym) { const [y,m]=ym.split('-').map(Number); return m===12?`${y+1}-01`:`${y}-${String(m+1).padStart(2,'0')}`; }
function fmtMonth(ym)  { const [y,m]=ym.split('-').map(Number); return `${TH_MONTHS[m-1]} ${y+543}`; } // Thai พ.ศ. — printed docs only
const EN_MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function fmtMonthEn(ym){ const [y,m]=ym.split('-').map(Number); return `${EN_MONTHS_SHORT[m-1]} ${y}`; } // on-screen "Jul 2026"
const LEAVE_TYPES = ['ลาพักร้อน','ลาป่วย','ลากิจ'];
const EXPENSE_CATS = ['ค่าเดินทาง','ค่าอาหาร','ค่าวัสดุ/อุปกรณ์','ค่าที่พัก','ค่าส่งพัสดุ','ค่าอินเตอร์เน็ต','ค่าซอฟต์แวร์','ค่าออฟฟิศ','อื่นๆ'];
const DAILY_EXP_CATS = ['ค่าส่งพัสดุ','ค่าอาหาร','ค่าที่พัก','ค่าอินเตอร์เน็ต','อื่นๆ'];

function DailyExpenseEditor({ items = [], onChange }) {
  const add = () => onChange([...items, { label: DAILY_EXP_CATS[0], amount: '' }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, k, v) => onChange(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  return (
    <div className="space-y-1.5">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <select value={it.label} onChange={e => update(i, 'label', e.target.value)}
            className="flex-1 bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-[11px] text-gray-900 outline-none focus:border-gray-400">
            {DAILY_EXP_CATS.map(c => <option key={c}>{c}</option>)}
          </select>
          <div className="relative w-24 flex-shrink-0">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">฿</span>
            <input type="number" min="0" placeholder="0" value={it.amount}
              onChange={e => update(i, 'amount', e.target.value)}
              className="w-full bg-white border border-gray-200 rounded-lg pl-5 pr-2 py-1.5 text-[11px] text-gray-900 outline-none focus:border-gray-400"
            />
          </div>
          <button type="button" onClick={() => remove(i)} className="text-gray-300 hover:text-red-400 flex-shrink-0">
            <XCircle size={14}/>
          </button>
        </div>
      ))}
      <button type="button" onClick={add}
        className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-gray-700 transition-colors">
        <Plus size={11}/> เพิ่มรายการ
      </button>
    </div>
  );
}

// ── small reusable components ────────────────────────────────────────────────
function Badge({ status }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.pending;
  return (
    <span style={{ background: s.bg, color: s.text }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium">
      {s.icon}{s.label}
    </span>
  );
}

function LocBadge({ loc }) {
  const c = LOC_COLOR[loc];
  if (!c) return null;
  return (
    <span style={{ background: c.bg, color: c.text }}
      className="inline-block px-2 py-0.5 rounded text-[10px] font-medium">
      {c.label}
    </span>
  );
}

function SectionCard({ children, className = '' }) {
  return (
    <div className={`bg-[#f8f8f8] rounded-xl p-4 ${className}`}>{children}</div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
export default function MyProfilePage() {
  const { user, isAdmin, changePassword } = useAuth();
  const today = toDateStr(new Date());

  // ── tabs ──────────────────────────────────────────────────────────────────
  const [tab, setTab] = useState('attendance');
  const adminTabs = ['attendance', 'approve', 'att_summary', 'expenses_admin', 'vouchers_admin', 'accounts_admin', 'profile'];
  const staffTabs = ['attendance', 'expenses', 'profile'];
  const tabs = isAdmin() ? adminTabs : staffTabs;

  // ── attendance state ──────────────────────────────────────────────────────
  const [todayLog, setTodayLog]   = useState(null);
  const [logs, setLogs]           = useState([]);
  const [locChoice, setLocChoice] = useState('office');
  const [onsiteName, setOnsiteName] = useState('');
  const [ciLoading, setCiLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(true);

  // ── retro log form ────────────────────────────────────────────────────────
  const yesterday = toDateStr(new Date(Date.now() - 86400000));
  const [showRetroForm, setShowRetroForm] = useState(false);
  const [retroForm, setRetroForm] = useState({ date: yesterday, checkInTime: '09:00', checkOutTime: '18:00', location: 'office', locationName: '', note: '' });
  const [retroSubmitting, setRetroSubmitting] = useState(false);
  const [allowanceEdits, setAllowanceEdits] = useState({}); // { [logId]: { travelCost, fieldAllowance } }

  // ── admin approval state ──────────────────────────────────────────────────
  const [pendingLogs, setPendingLogs] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);

  // ── admin attendance summary (per-person monthly) ─────────────────────────
  const [allLogs, setAllLogs]       = useState([]);
  const [allLeaves, setAllLeaves]   = useState([]);
  const [attSumLoading, setAttSumLoading] = useState(false);
  const [attMonth, setAttMonth]     = useState(today.slice(0, 7));
  const [attPerson, setAttPerson]   = useState(null); // userId — แตะแถวเพื่อดูปฏิทินรายวัน
  // แตะวันในปฏิทิน → แก้/ยกเลิกรายการที่อนุมัติแล้ว (admin)
  const [attEdit, setAttEdit]       = useState(null); // { kind:'log'|'leave', rec, personName, form }
  const [attEditSaving, setAttEditSaving] = useState(false);
  // เปลี่ยนเดือนแล้วปิดปฏิทินที่กางอยู่
  useEffect(() => { setAttPerson(null); }, [attMonth]);

  // ── staff's own leave requests ────────────────────────────────────────────
  const [staffLeaves, setStaffLeaves] = useState([]);
  const [empInfo, setEmpInfo] = useState({ startDate: '', probationMonths: DEFAULT_PROBATION_MONTHS });

  // ── leave form ────────────────────────────────────────────────────────────
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ type: 'ลาพักร้อน', startDate: today, endDate: today, days: 1, reason: '' });
  const [leaveSubmitting, setLeaveSubmitting] = useState(false);

  // ── expenses state ────────────────────────────────────────────────────────
  const [expenses, setExpenses]       = useState([]);
  const [expLoading, setExpLoading]   = useState(true);
  const [showExpForm, setShowExpForm] = useState(false);
  const [expForm, setExpForm]         = useState({ category: 'ค่าเดินทาง', amount: '', description: '', date: today, project: '', receiptStatus: 'none' });
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploadingReceiptId, setUploadingReceiptId] = useState(null);
  const [expSubmitting, setExpSubmitting] = useState(false);
  const fileRef = useRef();

  // Autocomplete for the claim form, built from this user's own past claims —
  // most frequently claimed first (e.g. "Vercel" every month).
  const expSuggest = useMemo(() => {
    const topValues = (key) => {
      const counts = new Map();
      for (const e of expenses) {
        const v = (e?.[key] || '').trim();
        if (v) counts.set(v, (counts.get(v) || 0) + 1);
      }
      return [...counts.entries()]
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .slice(0, 50)
        .map(([v]) => v);
    };
    return { descriptions: topValues('description'), projects: topValues('project') };
  }, [expenses]);

  // ── all expenses for admin ────────────────────────────────────────────────
  const [allExpenses, setAllExpenses] = useState([]);
  const [allExpLoading, setAllExpLoading] = useState(false);
  const [expMonth, setExpMonth] = useState(today.slice(0, 7));
  const [expStatusFilter, setExpStatusFilter] = useState('all');
  const [expPersonFilter, setExpPersonFilter] = useState(null); // userId — ดูรายละเอียดรายคน
  // เปลี่ยนเดือนแล้วกลับไปดูภาพรวมทุกคน (คนที่เลือกอาจไม่มีรายการในเดือนใหม่)
  useEffect(() => { setExpPersonFilter(null); }, [expMonth]);
  const [selectedForVoucher, setSelectedForVoucher] = useState(new Set());
  const [vouchers, setVouchers] = useState([]);
  const [allVouchers, setAllVouchers] = useState([]);
  const [vouchersLoading, setVouchersLoading] = useState(false);

  // ── bulk transfer (admin) ─────────────────────────────────────────────────
  const [selectedForTransfer, setSelectedForTransfer] = useState(new Set());
  const [bulkTransferDate, setBulkTransferDate] = useState(today);
  const [bulkTransferFile, setBulkTransferFile] = useState(null);
  const [bulkTransferring, setBulkTransferring] = useState(false);
  const bulkSlipRef = useRef(null);

  // ── edit rejected log ─────────────────────────────────────────────────────
  const [editLog, setEditLog]           = useState(null);
  const [editForm, setEditForm]         = useState({});
  const [editSubmitting, setEditSubmitting] = useState(false);

  // ── password change ───────────────────────────────────────────────────────
  const [pwForm, setPwForm]     = useState({ current: '', next: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);

  // ── user profile (real name + bank info) ──────────────────────────────────
  const [profile, setProfile]       = useState({ firstName: '', lastName: '', phone: '', bankName: '', bankAccount: '', bankAccountName: '' });
  const [profileSaving, setProfileSaving] = useState(false);

  // ── voucher preview modal ─────────────────────────────────────────────────
  const [voucherPreviewData, setVoucherPreviewData] = useState(null); // null = closed
  const [voucherCreating, setVoucherCreating]       = useState(false);

  // ── all user profiles (admin) ─────────────────────────────────────────────
  const [allProfiles, setAllProfiles]           = useState([]);
  const [allProfilesLoading, setAllProfilesLoading] = useState(false);

  // ── fetch on mount ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    loadAttendance();
    loadExpenses();
    loadVouchers();
    loadProfile();
    loadStaffLeaves();
    if (isAdmin()) { loadPending(); loadAllExpenses(); loadAllVouchersAdmin(); loadAllProfiles(); loadAttSummary(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function loadAttendance() {
    setLogsLoading(true);
    try {
      const [tl, ls] = await Promise.all([
        getTodayLog(user.id, today),
        getUserLogs(user.id, 30),
      ]);
      setTodayLog(tl);
      setLogs(ls);
    } catch (e) { toast.error('โหลดข้อมูลเวลาไม่สำเร็จ'); }
    setLogsLoading(false);
  }

  async function loadPending() {
    setPendingLoading(true);
    try {
      const [logs, leaves] = await Promise.all([getAllPendingLogs(), getAllPendingLeave()]);
      setPendingLogs(logs);
      setPendingLeaves(leaves);
    } catch { toast.error('โหลดรายการอนุมัติไม่สำเร็จ'); }
    setPendingLoading(false);
  }

  async function loadAttSummary() {
    setAttSumLoading(true);
    try {
      const [logs, leaves] = await Promise.all([
        (async () => {
          const { collection, getDocs } = await import('firebase/firestore');
          const { db } = await import('@/lib/firebase');
          const snap = await getDocs(collection(db, 'time_logs'));
          return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        })(),
        getAllLeaves(),
      ]);
      setAllLogs(logs);
      setAllLeaves(leaves);
    } catch { toast.error('โหลดสรุปการเข้างานไม่สำเร็จ'); }
    setAttSumLoading(false);
  }

  function openAttEdit(kind, rec, personName) {
    const form = kind === 'log'
      ? { location: rec.location || 'office', locationName: rec.locationName || '', note: rec.note || '' }
      : { type: rec.type || 'ลาพักร้อน', startDate: rec.startDate || '', endDate: rec.endDate || '' };
    setAttEdit({ kind, rec, personName, form });
  }

  async function handleAttCancel() {
    if (!attEdit) return;
    const { kind, rec, personName } = attEdit;
    setAttEditSaving(true);
    try {
      if (kind === 'leave') {
        await cancelLeaveRequest(rec.id, user.name);
        logActivity(user.id, user.name, 'leave_reject', `ยกเลิกใบลา ${personName}: ${rec.type} ${rec.startDate}${rec.endDate !== rec.startDate ? '–'+rec.endDate : ''}`);
        sendNotification({ type: 'leave_reject', title: 'ใบลาถูกยกเลิก', body: `${user.name} ยกเลิกใบลา ${rec.type} ${formatDate(rec.startDate)}${rec.endDate !== rec.startDate ? ' – '+formatDate(rec.endDate) : ''}`, targetUserIds: [rec.userId], data: { url: '/profile' } });
      } else {
        await cancelLog(rec.id, user.name);
        logActivity(user.id, user.name, 'attendance_reject', `ยกเลิกบันทึกเวลา ${personName}: ${rec.date}`);
        sendNotification({ type: 'attendance_reject', title: 'บันทึกเวลาถูกยกเลิก', body: `${user.name} ยกเลิกบันทึกเวลาวันที่ ${formatDate(rec.date)}`, targetUserIds: [rec.userId], data: { url: '/profile' } });
      }
      toast.success('ยกเลิกรายการแล้ว');
      setAttEdit(null);
      await loadAttSummary();
    } catch { toast.error('ยกเลิกไม่สำเร็จ'); }
    setAttEditSaving(false);
  }

  async function handleAttSave() {
    if (!attEdit) return;
    const { kind, rec, personName, form } = attEdit;
    setAttEditSaving(true);
    try {
      if (kind === 'leave') {
        if (!form.startDate || !form.endDate || form.endDate < form.startDate) { toast.error('ช่วงวันที่ไม่ถูกต้อง'); setAttEditSaving(false); return; }
        const days = Math.round((new Date(form.endDate) - new Date(form.startDate)) / 86400000) + 1;
        await updateLeaveRequest(rec.id, { type: form.type, startDate: form.startDate, endDate: form.endDate, days }, user.name);
        logActivity(user.id, user.name, 'leave_approve', `แก้ไขใบลา ${personName}: ${form.type} ${form.startDate}–${form.endDate}`);
        sendNotification({ type: 'leave_approve', title: 'ใบลาถูกแก้ไข', body: `${user.name} แก้ไขใบลาเป็น ${form.type} ${formatDate(form.startDate)}${form.endDate !== form.startDate ? ' – '+formatDate(form.endDate) : ''}`, targetUserIds: [rec.userId], data: { url: '/profile' } });
      } else {
        await adminUpdateLog(rec.id, { location: form.location, locationName: form.location === 'onsite' ? form.locationName : '', note: form.note }, user.name);
        logActivity(user.id, user.name, 'attendance_approve', `แก้ไขบันทึกเวลา ${personName}: ${rec.date} → ${LOC_COLOR[form.location]?.label}`);
        sendNotification({ type: 'attendance_approve', title: 'บันทึกเวลาถูกแก้ไข', body: `${user.name} แก้ไขบันทึกวันที่ ${formatDate(rec.date)} เป็น ${LOC_COLOR[form.location]?.label}`, targetUserIds: [rec.userId], data: { url: '/profile' } });
      }
      toast.success('บันทึกการแก้ไขแล้ว');
      setAttEdit(null);
      await loadAttSummary();
    } catch { toast.error('บันทึกไม่สำเร็จ'); }
    setAttEditSaving(false);
  }

  async function loadExpenses() {
    setExpLoading(true);
    try {
      const data = await getUserExpenseClaims(user.id);
      data.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      setExpenses(data);
    }
    catch { toast.error('โหลดรายการเบิกจ่ายไม่สำเร็จ'); }
    setExpLoading(false);
  }

  async function loadAllExpenses() {
    setAllExpLoading(true);
    try {
      const { collection, getDocs } = await import('firebase/firestore');
      const { db } = await import('@/lib/firebase');
      const snap = await getDocs(collection(db, 'expense_claims'));
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      setAllExpenses(data);
    } catch { toast.error('โหลดรายการเบิกจ่ายทั้งหมดไม่สำเร็จ'); }
    setAllExpLoading(false);
  }

  async function loadAllProfiles() {
    setAllProfilesLoading(true);
    try { setAllProfiles(await getAllUserProfiles()); }
    catch { toast.error('โหลดข้อมูลบัญชีไม่สำเร็จ'); }
    setAllProfilesLoading(false);
  }

  // ── check-in / check-out ──────────────────────────────────────────────────
  async function handleCheckIn() {
    setCiLoading(true);
    try {
      const res = await checkIn(user.id, user.name, locChoice, onsiteName);
      if (!res.success) { toast.error(res.error); return; }
      toast.success('เช็คอินสำเร็จ — รอแอดมินอนุมัติ');
      logActivity(user.id, user.name, 'checkin', `${locChoice === 'office' ? 'Office' : locChoice === 'wfh' ? 'WFH' : onsiteName || 'On-site'}`);
      await loadAttendance();
    } catch { toast.error('เช็คอินไม่สำเร็จ'); }
    setCiLoading(false);
  }

  async function handleCheckOut() {
    if (!todayLog) return;
    setCiLoading(true);
    try {
      await checkOut(todayLog.id, todayLog.checkIn);
      toast.success('เช็คเอาท์สำเร็จ — รอแอดมินอนุมัติ');
      logActivity(user.id, user.name, 'checkout', today);
      await loadAttendance();
    } catch { toast.error('เช็คเอาท์ไม่สำเร็จ'); }
    setCiLoading(false);
  }

  function openEditLog(l) {
    function tsToTime(ts, fallback = '09:00') {
      if (!ts) return fallback;
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
    }
    setEditLog(l);
    setEditForm({
      checkInTime:  tsToTime(l.checkIn),
      checkOutTime: tsToTime(l.checkOut, '18:00'),
      location:     l.location || 'office',
      locationName: l.locationName || '',
      note:         l.note || '',
    });
  }

  async function handleEditSubmit(e) {
    e.preventDefault();
    setEditSubmitting(true);
    try {
      const res = await resubmitLog(editLog.id, { date: editLog.date, ...editForm });
      if (!res.success) { toast.error(res.error); setEditSubmitting(false); return; }
      toast.success('ส่งใหม่เรียบร้อย — รออนุมัติ');
      sendNotification({ type: 'retro_log', title: `${user.name} แก้ไขบันทึกเวลาและส่งใหม่`, body: `วันที่ ${editLog.date} — รออนุมัติ`, targetUserIds: ADMIN_IDS.filter(id => id !== user.id), data: { url: '/profile' } });
      setEditLog(null);
      await loadAttendance();
    } catch { toast.error('ส่งใหม่ไม่สำเร็จ'); }
    setEditSubmitting(false);
  }

  async function handleRetroSubmit(e) {
    e.preventDefault();
    setRetroSubmitting(true);
    try {
      const res = await submitRetroLog(user.id, user.name, retroForm);
      if (!res.success) { toast.error(res.error); setRetroSubmitting(false); return; }
      toast.success('บันทึกย้อนหลังสำเร็จ — รอแอดมินอนุมัติ');
      logActivity(user.id, user.name, 'retro_log', retroForm.date);
      sendNotification({ type: 'retro_log', title: `${user.name} บันทึกเวลาย้อนหลัง`, body: `วันที่ ${retroForm.date} — รอการอนุมัติ`, targetUserIds: ADMIN_IDS.filter(id => id !== user.id), data: { url: '/profile' } });
      setShowRetroForm(false);
      setRetroForm({ date: yesterday, checkInTime: '09:00', checkOutTime: '18:00', location: 'office', locationName: '', note: '' });
      await loadAttendance();
    } catch { toast.error('บันทึกย้อนหลังไม่สำเร็จ'); }
    setRetroSubmitting(false);
  }

  async function handleSaveAllowance(logId) {
    const vals = allowanceEdits[logId] || {};
    try {
      await updateLogAllowances(logId, {
        ...vals,
        dailyExpenses: vals.dailyExpenses,
      });
      toast.success('บันทึกแล้ว');
      await loadPending();
    } catch { toast.error('บันทึกไม่สำเร็จ'); }
  }

  // ── approval actions ──────────────────────────────────────────────────────
  async function handleApprove(logId, targetUserId) {
    try {
      await approveLog(logId, user.name);
      toast.success('อนุมัติแล้ว');
      logActivity(user.id, user.name, 'attendance_approve', targetUserId || '');
      if (targetUserId) sendNotification({ type: 'attendance_approved', title: 'เวลาทำงานได้รับการอนุมัติ', body: `${user.name} อนุมัติบันทึกเวลาของคุณแล้ว`, targetUserIds: [targetUserId], data: { type: 'attendance_approved', url: '/profile' } });
      await loadPending();
    } catch { toast.error('อนุมัติไม่สำเร็จ'); }
  }

  async function handleReject(logId, targetUserId) {
    try {
      await rejectLog(logId, user.name);
      toast.success('ปฏิเสธแล้ว');
      logActivity(user.id, user.name, 'attendance_reject', targetUserId || '');
      if (targetUserId) sendNotification({ type: 'attendance_rejected', title: 'บันทึกเวลาไม่ผ่านการอนุมัติ', body: `${user.name} ไม่อนุมัติบันทึกเวลาของคุณ`, targetUserIds: [targetUserId], data: { type: 'attendance_rejected', url: '/profile' } });
      await loadPending();
    } catch { toast.error('ปฏิเสธไม่สำเร็จ'); }
  }

  async function handleLeaveApprove(leaveId, targetUserId) {
    try {
      await updateDoc(doc(db, 'leave_requests', leaveId), { status: 'approved', approvedBy: user.name, approvedAt: serverTimestamp() });
      toast.success('อนุมัติการลาแล้ว');
      logActivity(user.id, user.name, 'leave_approve', targetUserId || '');
      if (targetUserId) sendNotification({ type: 'leave_approved', title: 'คำขอลาได้รับการอนุมัติ', body: `${user.name} อนุมัติคำขอลาของคุณแล้ว`, targetUserIds: [targetUserId], data: { type: 'leave_approved', url: '/profile' } });
      await loadPending();
    } catch { toast.error('อนุมัติไม่สำเร็จ'); }
  }

  async function handleLeaveReject(leaveId, targetUserId) {
    try {
      await updateDoc(doc(db, 'leave_requests', leaveId), { status: 'rejected', approvedBy: user.name, approvedAt: serverTimestamp() });
      toast.success('ปฏิเสธการลาแล้ว');
      logActivity(user.id, user.name, 'leave_reject', targetUserId || '');
      if (targetUserId) sendNotification({ type: 'leave_rejected', title: 'คำขอลาไม่ผ่านการอนุมัติ', body: `${user.name} ไม่อนุมัติคำขอลาของคุณ`, targetUserIds: [targetUserId], data: { type: 'leave_rejected', url: '/profile' } });
      await loadPending();
    } catch { toast.error('ปฏิเสธไม่สำเร็จ'); }
  }

  async function handleApproveAll() {
    const logs = pendingLogs;
    await Promise.all(logs.map((l) => approveLog(l.id, user.name)));
    toast.success(`อนุมัติ ${logs.length} รายการ`);
    const userIds = [...new Set(logs.map((l) => l.userId).filter(Boolean))];
    if (userIds.length > 0) sendNotification({ type: 'attendance_approved', title: 'เวลาทำงานได้รับการอนุมัติ', body: `${user.name} อนุมัติบันทึกเวลาทั้งหมดของคุณแล้ว`, targetUserIds: userIds, data: { type: 'attendance_approved', url: '/profile' } });
    await loadPending();
  }

  // ── leave request ─────────────────────────────────────────────────────────
  async function handleLeaveSubmit(e) {
    e.preventDefault();
    setLeaveSubmitting(true);
    try {
      await submitLeaveRequest(user.id, user.name, leaveForm);
      toast.success('ส่งคำขอลาสำเร็จ');
      logActivity(user.id, user.name, 'leave_request', `${leaveForm.type} ${leaveForm.startDate}${leaveForm.endDate !== leaveForm.startDate ? '–'+leaveForm.endDate : ''} (${leaveForm.days} วัน)`);
      sendNotification({ type: 'leave_request', title: `${user.name} ยื่นใบลา`, body: `${leaveForm.type} ${leaveForm.days} วัน — "${leaveForm.reason || 'ไม่ระบุเหตุผล'}"`, targetUserIds: ADMIN_IDS.filter(id => id !== user.id), data: { url: '/profile' } });
      setShowLeaveForm(false);
      setLeaveForm({ type: 'ลาพักร้อน', startDate: today, endDate: today, days: 1, reason: '' });
    } catch { toast.error('ส่งคำขอลาไม่สำเร็จ'); }
    setLeaveSubmitting(false);
  }

  // ── expense claim ─────────────────────────────────────────────────────────
  async function handleExpSubmit(e) {
    e.preventDefault();
    if (user?.id === 'admin') { toast.error('บัญชีกลาง admin เบิกไม่ได้ — ล็อกอินด้วยชื่อตัวเอง'); return; }
    const amt = Number(expForm.amount);
    if (!expForm.description.trim()) { toast.error('กรุณาใส่รายละเอียดรายการ'); return; }
    if (!expForm.amount) { toast.error('กรุณากรอกจำนวนเงิน'); return; }
    if (amt <= 0) { toast.error('จำนวนเงินต้องมากกว่า 0'); return; }
    if (amt > 100000) { toast.error('จำนวนเงินเกิน 100,000 บาท — กรุณาติดต่อแอดมินโดยตรง'); return; }
    if (!expForm.date) { toast.error('กรุณาเลือกวันที่ใช้จ่าย'); return; }
    if (expForm.date > today) { toast.error('วันที่ใช้จ่ายต้องไม่ใช่วันในอนาคต'); return; }
    const submittedMonth = expForm.date.slice(0, 7);
    setExpSubmitting(true);
    try {
      await submitExpenseClaim(user.id, user.name, expForm, receiptFile);
      toast.success('ส่งคำขอเบิกสำเร็จ');
      logActivity(user.id, user.name, 'expense_claim', `${expForm.category} ฿${Number(expForm.amount).toLocaleString()}${expForm.project ? ' — '+expForm.project : ''}`);
      sendNotification({ type: 'expense_claim', title: `${user.name} เบิกค่าใช้จ่าย`, body: `${expForm.category} ฿${Number(expForm.amount).toLocaleString()} — ${expForm.description}`, targetUserIds: ADMIN_IDS.filter(id => id !== user.id), data: { url: '/profile' } });
      setShowExpForm(false);
      setExpForm({ category: 'ค่าเดินทาง', amount: '', description: '', date: today, project: '', receiptStatus: 'none' });
      setReceiptFile(null);
      setExpMonth(submittedMonth);
      await loadExpenses();
      if (isAdmin()) await loadAllExpenses();
    } catch (err) { toast.error('ส่งคำขอเบิกไม่สำเร็จ'); console.error(err); }
    setExpSubmitting(false);
  }

  async function handleExpApprove(id, targetUserId) {
    try {
      await approveExpense(id, user.name);
      toast.success('อนุมัติแล้ว');
      logActivity(user.id, user.name, 'expense_approve', targetUserId || '');
      if (targetUserId) sendNotification({ type: 'expense_approved', title: 'คำขอเบิกเงินได้รับการอนุมัติ', body: `${user.name} อนุมัติคำขอเบิกเงินของคุณแล้ว`, targetUserIds: [targetUserId], data: { type: 'expense_approved', url: '/profile' } });
      await loadAllExpenses();
    } catch { toast.error('ไม่สำเร็จ'); }
  }
  async function handleExpReject(id, targetUserId) {
    try {
      await rejectExpense(id, user.name);
      toast.success('ปฏิเสธแล้ว');
      logActivity(user.id, user.name, 'expense_reject', targetUserId || '');
      if (targetUserId) sendNotification({ type: 'expense_rejected', title: 'คำขอเบิกเงินไม่ผ่านการอนุมัติ', body: `${user.name} ไม่อนุมัติคำขอเบิกเงินของคุณ`, targetUserIds: [targetUserId], data: { type: 'expense_rejected', url: '/profile' } });
      await loadAllExpenses();
    } catch { toast.error('ไม่สำเร็จ'); }
  }
  async function handleExpRevert(id, targetUserId) {
    if (!window.confirm('ปรับรายการนี้กลับเป็น “รออนุมัติ”? การอนุมัติเดิมจะถูกยกเลิก')) return;
    try {
      await revertExpenseToPending(id, user.name);
      toast.success('กลับเป็นรออนุมัติแล้ว');
      logActivity(user.id, user.name, 'expense_reject', `ปรับกลับรออนุมัติ ${targetUserId || ''}`);
      if (targetUserId) sendNotification({ type: 'expense_rejected', title: 'คำขอเบิกเงินถูกปรับสถานะ', body: `${user.name} ปรับคำขอเบิกของคุณกลับเป็น “รออนุมัติ”`, targetUserIds: [targetUserId], data: { url: '/profile' } });
      await loadAllExpenses();
    } catch { toast.error('ไม่สำเร็จ'); }
  }

  async function handleReceiptUpload(claimId, targetUserId, file) {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('ไฟล์ใหญ่เกิน 10MB'); return; }
    setUploadingReceiptId(claimId);
    try {
      await uploadExpenseReceipt(claimId, targetUserId || user.id, file);
      toast.success('แนบใบเสร็จแล้ว');
      await loadExpenses();
      if (isAdmin()) await loadAllExpenses();
    } catch { toast.error('อัพโหลดไม่สำเร็จ'); }
    setUploadingReceiptId(null);
  }

  async function handleReceiptClear(claimId) {
    if (!window.confirm('ลบสลิป/ใบเสร็จที่แนบไว้? แล้วแนบใหม่ได้')) return;
    setUploadingReceiptId(claimId);
    try {
      await clearExpenseReceipt(claimId);
      toast.success('ลบใบเสร็จแล้ว — แนบใหม่ได้');
      await loadExpenses();
      if (isAdmin()) await loadAllExpenses();
    } catch { toast.error('ลบไม่สำเร็จ'); }
    setUploadingReceiptId(null);
  }

  async function handleExpDelete(id, status) {
    if (status === 'approved' || status === 'transferred') { toast.error('ลบได้เฉพาะรายการที่ยังไม่อนุมัติ'); return; }
    if (!window.confirm('ลบรายการเบิกจ่ายนี้ถาวร? กู้คืนไม่ได้')) return;
    try {
      const res = await deleteExpenseClaim(id);
      if (res && res.success === false) { toast.error(res.error || 'ลบไม่สำเร็จ'); return; }
      toast.success('ลบรายการแล้ว');
      await loadExpenses();
      if (isAdmin()) await loadAllExpenses();
    } catch { toast.error('ลบไม่สำเร็จ'); }
  }

  async function loadProfile() {
    try {
      const p = await getUserProfile(user.id);
      if (p) {
        setProfile({ firstName: p.firstName || '', lastName: p.lastName || '', phone: p.phone || '', bankName: p.bankName || '', bankAccount: p.bankAccount || '', bankAccountName: p.bankAccountName || '' });
        setEmpInfo({ startDate: p.start_date || '', probationMonths: Number(p.probation_months) || DEFAULT_PROBATION_MONTHS });
      }
    } catch {}
  }

  async function loadStaffLeaves() {
    try { setStaffLeaves(await getUserLeaveRequests(user.id)); } catch {}
  }

  async function handleProfileSave(e) {
    e.preventDefault();
    setProfileSaving(true);
    try {
      await saveUserProfile(user.id, profile);
      await updateTalentProfile(user.id, { firstName: profile.firstName, lastName: profile.lastName, phone: profile.phone });
      toast.success('บันทึกข้อมูลส่วนตัวแล้ว');
    } catch { toast.error('บันทึกไม่สำเร็จ'); }
    setProfileSaving(false);
  }

  async function loadVouchers() {
    try { setVouchers(await getVouchersByStaff(user.id)); } catch {}
  }
  async function loadAllVouchersAdmin() {
    try { setAllVouchers(await getAllVouchers()); } catch {}
  }

  async function handleExpEdit(id, data) {
    await updateExpenseClaim(id, data);
    setAllExpenses(prev => prev.map(e => e.id === id ? { ...e, ...data, amount: Number(data.amount ?? e.amount) } : e));
    setExpenses(prev => prev.map(e => e.id === id ? { ...e, ...data, amount: Number(data.amount ?? e.amount) } : e));
  }

  async function handleTransfer(id, targetUserId, file, date) {
    try {
      await markExpenseTransferred(id, user.name, file, date);
      toast.success('บันทึกการโอนสำเร็จ');
      logActivity(user.id, user.name, 'expense_transferred', targetUserId || '');
      if (targetUserId) sendNotification({ type: 'expense_transferred', title: 'บริษัทโอนเงินคืนแล้ว', body: 'มีการโอนเงินสำรองจ่ายคืนให้คุณแล้ว — กรุณาตรวจสอบ', targetUserIds: [targetUserId], data: { url: '/profile' } });
      await loadAllExpenses();
    } catch { toast.error('ไม่สำเร็จ'); }
  }

  async function handleBulkTransfer() {
    if (!bulkTransferDate) { toast.error('กรุณาเลือกวันที่โอน'); return; }
    setBulkTransferring(true);
    try {
      const ids = [...selectedForTransfer];
      const selItems = allExpenses.filter(e => selectedForTransfer.has(e.id));
      const targetUserIds = [...new Set(selItems.map(e => e.userId))];
      await bulkMarkExpenseTransferred(ids, user.name, bulkTransferFile, bulkTransferDate);
      toast.success(`บันทึกการโอน ${ids.length} รายการแล้ว`);
      setSelectedForTransfer(new Set());
      setBulkTransferFile(null);
      logActivity(user.id, user.name, 'expense_bulk_transferred', `${ids.length} รายการ`);
      targetUserIds.forEach(tid => sendNotification({ type: 'expense_transferred', title: 'บริษัทโอนเงินคืนแล้ว', body: `${user.name} โอนเงินสำรองจ่ายคืนให้คุณแล้ว — กรุณาตรวจสอบ`, targetUserIds: [tid], data: { url: '/profile' } }));
      await loadAllExpenses();
    } catch { toast.error('บันทึกการโอนไม่สำเร็จ'); }
    setBulkTransferring(false);
  }

  async function handleCreateVoucher() {
    const selected = allExpenses.filter(e => selectedForVoucher.has(e.id));
    if (!selected.length) return;
    const staffIds = [...new Set(selected.map(e => e.userId))];
    if (staffIds.length > 1) { toast.error('กรุณาเลือกรายการจาก staff คนเดียวกัน'); return; }
    const staffId = staffIds[0];
    const staffName = selected[0].userName;
    const month = selected[0]?.date?.slice(0, 7) || new Date().toISOString().slice(0, 7);
    const transferDates = selected.map(e => e.transferDate).filter(Boolean).sort();
    const transferDate = transferDates[transferDates.length - 1] || '';
    const [staffProf, adminProf, voucherNo] = await Promise.all([
      getUserProfile(staffId).catch(() => null),
      getUserProfile(user.id).catch(() => null),
      getNextVoucherNo(month),
    ]);
    setVoucherPreviewData({ items: selected, staffId, staffName, staffProfile: staffProf || {}, adminProfile: adminProf || {}, adminName: user.name, voucherNo, transferDate });
  }

  async function handleCreateVoucherConfirm(finalItems, transferDate) {
    if (!voucherPreviewData || !finalItems.length) { toast.error('ต้องมีอย่างน้อย 1 รายการ'); return; }
    const { staffId, staffName, staffProfile, adminProfile, voucherNo } = voucherPreviewData;
    setVoucherCreating(true);
    try {
      const sp = staffProfile; const ap = adminProfile;
      const extras = {
        voucherNo,
        transferDate: transferDate || '',
        staffFullName: (sp.firstName && sp.lastName) ? `${sp.firstName} ${sp.lastName}` : staffName,
        adminFullName: (ap.firstName && ap.lastName) ? `${ap.firstName} ${ap.lastName}` : user.name,
        staffBankName: sp.bankName || '',
        staffBankAccount: sp.bankAccount || '',
        staffBankAccountName: sp.bankAccountName || '',
        staffPhone: sp.phone || '',
      };
      const result = await createPaymentVoucher(user.id, user.name, staffId, staffName, finalItems, extras);
      toast.success(`สร้างใบสำคัญจ่าย ${result.voucherNo} แล้ว`);
      setSelectedForVoucher(new Set());
      setVoucherPreviewData(null);
      logActivity(user.id, user.name, 'voucher_created', `${result.voucherNo} — ${staffName}`);
      sendNotification({ type: 'voucher_created', title: 'ใบสำคัญจ่ายรอเซ็น', body: `${user.name} ออกใบสำคัญจ่าย ${result.voucherNo} — กรุณาตรวจสอบและยืนยันรับเงิน`, targetUserIds: [staffId], data: { url: '/profile' } });
      await loadAllVouchersAdmin();
    } catch { toast.error('สร้างใบสำคัญจ่ายไม่สำเร็จ'); }
    setVoucherCreating(false);
  }

  async function handleSignVoucher(voucherId) {
    try {
      await signVoucher(voucherId);
      toast.success('ยืนยันรับเงินแล้ว');
      await loadVouchers();
    } catch { toast.error('ไม่สำเร็จ'); }
  }

  async function handleToggleVoucherStatus(voucherId, currentStatus) {
    const next = currentStatus === 'signed' ? 'pending_signature' : 'signed';
    try {
      await setVoucherStatus(voucherId, next);
      toast.success(next === 'signed' ? 'เปลี่ยนเป็น เซ็นแล้ว' : 'เปลี่ยนเป็น รอเซ็น');
      setAllVouchers(prev => prev.map(v => v.id === voucherId ? { ...v, status: next } : v));
    } catch { toast.error('ไม่สำเร็จ'); }
  }

  async function handleDeleteVoucher(voucherId, voucherNo) {
    if (!window.confirm(`ลบใบสำคัญจ่าย ${voucherNo} ?\n(ไม่สามารถกู้คืนได้)`)) return;
    try {
      await deletePaymentVoucher(voucherId);
      toast.success(`ลบ ${voucherNo} แล้ว`);
      setAllVouchers(prev => prev.filter(v => v.id !== voucherId));
    } catch { toast.error('ลบไม่สำเร็จ'); }
  }

  function openVoucherPrint(v) {
    const issueDate = new Date().toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' });
    const taxItems = (v.items || []).filter(it => it.receiptStatus === 'tax_invoice');
    const staffFullName = v.staffFullName || v.staffName || '';
    const adminFullName = v.adminFullName || v.adminName || '';
    const totalAmt = Number(v.totalAmount).toLocaleString();

    const dot = (color) => `<span style="width:6px;height:6px;border-radius:50%;background:${color};display:inline-block;vertical-align:middle;margin-right:3px"></span>`;
    const receiptLabel = (s) => {
      if (s === 'tax_invoice') return `${dot('#f59e0b')}<span style="color:#92400e">ใบกำกับภาษี</span>`;
      if (s === 'receipt')     return `${dot('#16a34a')}<span style="color:#15803d">มีใบเสร็จ</span>`;
      return `${dot('#9ca3af')}<span style="color:#9ca3af">ไม่มีใบเสร็จ</span>`;
    };

    const rows = (v.items || []).map((it, i) => {
      const isTax = it.receiptStatus === 'tax_invoice';
      return `<tr style="${isTax ? 'background:#fffbeb' : ''}">
        <td style="text-align:center;color:#9ca3af">${i + 1}</td>
        <td>${isTax ? dot('#f59e0b') : ''}${it.description || '—'}</td>
        <td><span style="background:#f3f4f6;color:#374151;font-size:10px;padding:1px 6px;border-radius:3px">${it.category || '—'}</span></td>
        <td style="color:#9ca3af;font-size:11px">${it.project || '—'}</td>
        <td style="color:#9ca3af;font-size:11px">${it.date || '—'}</td>
        <td style="font-size:11px">${receiptLabel(it.receiptStatus)}</td>
        <td style="text-align:right;font-weight:600">฿${Number(it.amount).toLocaleString()}</td>
      </tr>`;
    }).join('');

    const taxBanner = '';

    const hasBankInfo = v.staffBankName || v.staffBankAccount;
    const bankSection = hasBankInfo ? `
      <div style="background:#f9fafb;border:0.5px solid #e5e7eb;border-radius:6px;padding:11px 14px">
        <div style="font-size:9px;font-weight:700;color:#374151;text-transform:uppercase;letter-spacing:.6px;margin-bottom:7px">ข้อมูลบัญชีรับโอน</div>
        <table style="width:100%;font-size:11px;border-collapse:collapse">
          ${v.staffBankName ? `<tr><td style="color:#9ca3af;padding:2px 0">ธนาคาร</td><td style="text-align:right;font-weight:600">${v.staffBankName}</td></tr>` : ''}
          ${v.staffBankAccount ? `<tr><td style="color:#9ca3af;padding:2px 0">เลขบัญชี</td><td style="text-align:right;font-weight:600">${v.staffBankAccount}</td></tr>` : ''}
          ${v.staffBankAccountName ? `<tr><td style="color:#9ca3af;padding:2px 0">ชื่อบัญชี</td><td style="text-align:right;font-weight:600">${v.staffBankAccountName}</td></tr>` : ''}
        </table>
      </div>` : '';

    const html = `<!DOCTYPE html><html lang="th"><head><meta charset="UTF-8">
<title>ใบสำคัญจ่าย ${v.voucherNo}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@400;600;700&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Sarabun',sans-serif;padding:32px 40px;color:#111;font-size:13px;line-height:1.6;background:#fff}
  .head{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:14px;border-bottom:1px solid #e5e7eb}
  .co-name{font-size:14px;font-weight:700}.co-sub{font-size:10px;color:#6b7280}.co-addr{font-size:9px;color:#9ca3af;margin-top:2px;line-height:1.5}
  .doc-title{font-size:20px;font-weight:700;text-align:right}.doc-en{font-size:10px;color:#9ca3af;text-align:right}
  .doc-no{margin-top:5px;text-align:right;display:inline-flex;align-items:center;gap:5px;background:#f3f4f6;border:0.5px solid #e5e7eb;border-radius:4px;padding:2px 8px;font-size:11px}
  .meta{display:grid;grid-template-columns:1fr 1fr;background:#f9fafb;border-top:1px solid #e5e7eb;border-bottom:1px solid #e5e7eb}
  .meta-l{padding:10px 16px;border-right:0.5px solid #e5e7eb}.meta-r{padding:10px 16px;display:grid;grid-template-columns:1fr 1fr;gap:4px 12px}
  .lbl{font-size:8px;color:#9ca3af;font-weight:700;text-transform:uppercase;letter-spacing:.5px;margin-bottom:2px}.val{font-size:11px;font-weight:700}
  table.vt{width:100%;border-collapse:collapse;font-size:12px}
  .vt thead tr{border-top:0.5px solid #e5e7eb;border-bottom:1.5px solid #374151}
  .vt th{padding:5px 8px;text-align:left;font-size:8px;font-weight:700;color:#374151;letter-spacing:.4px;text-transform:uppercase}
  .vt th.r{text-align:right}
  .vt td{padding:7px 8px;border-bottom:0.5px solid #f3f4f6;vertical-align:middle}
  .vt td.r{text-align:right;font-weight:600}
  .vt .tot td{font-weight:700;border-top:1px solid #e5e7eb;border-bottom:none;font-size:13px}
  .bottom{display:grid;grid-template-columns:${hasBankInfo ? '1fr 1fr' : '1fr'};gap:14px;padding:12px 16px;border-top:1px solid #e5e7eb}
  .amt-box{border:0.5px solid #d1d5db;border-radius:6px;padding:10px 14px;text-align:right;background:#f9fafb}
  .amt-lbl{font-size:8px;color:#9ca3af;text-transform:uppercase;letter-spacing:.5px;font-weight:700}
  .amt-val{font-size:22px;font-weight:700;letter-spacing:-.5px}
  .amt-words{font-size:9px;color:#6b7280;margin-top:2px}
  .sigs{display:grid;grid-template-columns:1fr 1fr;padding:12px 16px;border-top:0.5px solid #e5e7eb;gap:40px}
  .sig{text-align:center}.sig-line{border-bottom:0.5px solid #9ca3af;height:32px;margin:0 10px 7px}
  .sig-name{font-size:12px;font-weight:700}.sig-role{font-size:10px;color:#6b7280}.sig-role-en{font-size:9px;color:#9ca3af}
  .btn{display:block;margin:20px auto 0;padding:10px 32px;background:#111;color:#fff;border:none;border-radius:8px;cursor:pointer;font-size:13px;font-family:inherit}
  @media print{.btn{display:none}}
</style></head><body>

<div class="head">
  <div style="display:flex;align-items:flex-start;gap:12px">
    <img src="/LOGO_FYM-01.svg" alt="FLASHYOURMEME" style="width:100px;height:auto;flex-shrink:0">
    <div style="padding-top:2px">
      <div class="co-name">บริษัท แฟลชยัวร์มีม จำกัด</div>
      <div class="co-sub">เลขทะเบียนนิติบุคคล 0145565003261</div>
      <div class="co-addr">628/164 ถนนเลียบคลองรังสิต ตำบลประชาธิปัตย์ อำเภอธัญบุรี จังหวัดปทุมธานี 12130</div>
    </div>
  </div>
  <div style="text-align:right">
    <div class="doc-title">ใบสำคัญจ่าย</div>
    <div class="doc-en">Payment Voucher</div>
    <div style="text-align:right;margin-top:5px"><span class="doc-no"><span style="color:#6b7280">เลขที่</span><b>${v.voucherNo}</b></span></div>
  </div>
</div>

<div class="meta">
  <div class="meta-l">
    <div class="lbl">จ่ายให้</div>
    <div class="val">${staffFullName}</div>
    ${v.staffPhone ? `<div style="font-size:9px;color:#9ca3af;margin-top:1px">${v.staffPhone}</div>` : ''}
  </div>
  <div class="meta-r">
    <div><div class="lbl">วันที่ออก</div><div class="val" style="font-size:11px">${issueDate}</div></div>
    <div><div class="lbl">สำหรับเดือน</div><div class="val" style="font-size:11px">${fmtMonth(v.month || '')}</div></div>
    <div><div class="lbl">อนุมัติโดย</div><div class="val" style="font-size:11px">${adminFullName}</div></div>
    <div><div class="lbl">รวมรายการ</div><div class="val" style="font-size:11px">${(v.items||[]).length} รายการ</div></div>
    ${v.transferDate ? `<div style="grid-column:span 2"><div class="lbl">วันที่บริษัทโอนเงินคืน</div><div class="val" style="font-size:11px">${new Date(v.transferDate).toLocaleDateString('th-TH',{year:'numeric',month:'long',day:'numeric'})}</div></div>` : ''}
  </div>
</div>

${taxBanner}

<table class="vt">
  <thead><tr>
    <th style="width:22px">#</th><th>รายละเอียด</th>
    <th style="width:76px">หมวดหมู่</th><th style="width:58px">โปรเจกต์</th>
    <th style="width:50px">วันที่</th><th style="width:96px">หลักฐาน</th>
    <th class="r" style="width:70px">จำนวนเงิน</th>
  </tr></thead>
  <tbody>
    ${rows}
    <tr class="tot"><td colspan="6" style="text-align:right;color:#6b7280;font-size:10px;font-weight:400;padding-right:8px">รวมทั้งสิ้น</td><td class="r">฿${totalAmt}</td></tr>
  </tbody>
</table>

<div class="bottom">
  ${bankSection}
  <div class="amt-box">
    <div class="amt-lbl">รวมทั้งสิ้น · Total Amount</div>
    <div class="amt-val">฿${totalAmt}</div>
  </div>
</div>

<div class="sigs">
  <div class="sig"><div class="sig-line"></div><div class="sig-name">${staffFullName}</div><div class="sig-role">ผู้รับเงิน</div><div class="sig-role-en">Payee</div></div>
  <div class="sig"><div class="sig-line"></div><div class="sig-name">${adminFullName}</div><div class="sig-role">ผู้อนุมัติจ่าย</div><div class="sig-role-en">Authorised by</div></div>
</div>

<button class="btn" onclick="window.print()">พิมพ์ / บันทึก PDF</button>
</body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  }

  // ── password change ───────────────────────────────────────────────────────
  async function handlePwChange(e) {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) { toast.error('รหัสผ่านใหม่ไม่ตรงกัน'); return; }
    setPwLoading(true);
    const res = await changePassword(pwForm.current, pwForm.next);
    if (res.success) {
      toast.success('เปลี่ยนรหัสผ่านสำเร็จ');
      setPwForm({ current: '', next: '', confirm: '' });
    } else {
      toast.error(res.error);
    }
    setPwLoading(false);
  }

  // ── derived stats ─────────────────────────────────────────────────────────
  const approvedLogs = logs.filter((l) => l.status === 'approved');
  const locCounts = { office: 0, wfh: 0, onsite: 0 };
  approvedLogs.forEach((l) => { if (locCounts[l.location] !== undefined) locCounts[l.location]++; });
  const totalApproved = approvedLogs.length;
  const pendingToday = todayLog?.status === 'pending' ? 1 : 0;

  // ── tab label helpers ─────────────────────────────────────────────────────
  const TAB_LABELS = {
    attendance:     'เวลาทำงาน',
    approve:        'อนุมัติ',
    att_summary:     'สรุปการเข้างาน',
    expenses:       'เบิกจ่าย',
    expenses_admin:  'เบิกจ่าย',
    vouchers_admin:  'ใบสำคัญจ่าย',
    accounts_admin:  'ข้อมูลบัญชี',
    profile:         'บัญชี',
  };

  if (!user) return null;

  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="w-full">

      {/* ── edit rejected log modal ───────────────────────────────────────── */}
      {attEdit && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" onClick={() => setAttEdit(null)}>
          <div className="w-full max-w-md bg-white rounded-t-2xl p-5 space-y-4 pb-8" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[13px] font-semibold text-gray-900">
                  {attEdit.kind === 'leave' ? 'แก้ไข / ยกเลิกใบลา' : 'แก้ไข / ยกเลิกบันทึกเวลา'}
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">
                  {attEdit.personName}
                  {attEdit.kind === 'log' && ` · ${formatDate(attEdit.rec.date)}`}
                </div>
              </div>
              <button onClick={() => setAttEdit(null)} className="p-1 text-gray-400 hover:text-gray-700"><XCircle size={18}/></button>
            </div>

            {attEdit.kind === 'leave' ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-[10px] text-gray-400 mb-1">ประเภทการลา</label>
                  <select value={attEdit.form.type}
                    onChange={e => setAttEdit({ ...attEdit, form: { ...attEdit.form, type: e.target.value } })}
                    className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-gray-400">
                    {LEAVE_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] text-gray-400 mb-1">ตั้งแต่วันที่</label>
                    <input type="date" value={attEdit.form.startDate}
                      onChange={e => { const s = e.target.value; setAttEdit({ ...attEdit, form: { ...attEdit.form, startDate: s, endDate: attEdit.form.endDate < s ? s : attEdit.form.endDate } }); }}
                      className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-gray-400"/>
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-400 mb-1">ถึงวันที่</label>
                    <input type="date" value={attEdit.form.endDate} min={attEdit.form.startDate}
                      onChange={e => setAttEdit({ ...attEdit, form: { ...attEdit.form, endDate: e.target.value } })}
                      className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-gray-400"/>
                  </div>
                </div>
                <div className="text-[10px] text-gray-400">
                  รวม {attEdit.form.startDate && attEdit.form.endDate ? Math.max(1, Math.round((new Date(attEdit.form.endDate) - new Date(attEdit.form.startDate)) / 86400000) + 1) : 0} วัน
                  {attEdit.rec.reason ? ` · เหตุผลเดิม: ${attEdit.rec.reason}` : ''}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-[10px] text-gray-400 mb-1">สถานที่</label>
                  <div className="flex gap-2">
                    {['office','wfh','onsite'].map((loc) => (
                      <button key={loc} type="button"
                        onClick={() => setAttEdit({ ...attEdit, form: { ...attEdit.form, location: loc } })}
                        style={attEdit.form.location === loc ? { background: LOC_COLOR[loc].bg, borderColor: LOC_COLOR[loc].text, color: LOC_COLOR[loc].text } : {}}
                        className={`flex-1 py-2 rounded-xl text-[12px] border-2 font-medium transition-all ${attEdit.form.location === loc ? '' : 'border-gray-100 text-gray-400 bg-gray-50'}`}>
                        {LOC_COLOR[loc].label}
                      </button>
                    ))}
                  </div>
                </div>
                {attEdit.form.location === 'onsite' && (
                  <input placeholder="ระบุสถานที่ on-site..."
                    value={attEdit.form.locationName}
                    onChange={e => setAttEdit({ ...attEdit, form: { ...attEdit.form, locationName: e.target.value } })}
                    className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-gray-400"/>
                )}
                <div>
                  <label className="block text-[10px] text-gray-400 mb-1">หมายเหตุ (ถ้ามี)</label>
                  <input placeholder="เช่น เหตุผลที่แก้ไข..."
                    value={attEdit.form.note}
                    onChange={e => setAttEdit({ ...attEdit, form: { ...attEdit.form, note: e.target.value } })}
                    className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-gray-400"/>
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <button type="button" disabled={attEditSaving}
                onClick={() => { if (window.confirm(attEdit.kind === 'leave' ? 'ยกเลิกใบลานี้? รายการจะถูกนำออกจากยอดสรุป (กู้คืนได้ภายหลัง)' : 'ยกเลิกบันทึกเวลานี้? รายการจะถูกนำออกจากยอดสรุป (กู้คืนได้ภายหลัง)')) handleAttCancel(); }}
                className="px-4 py-2.5 bg-red-50 text-red-600 rounded-xl text-[12px] font-medium disabled:opacity-50">
                {attEdit.kind === 'leave' ? 'ยกเลิกใบลา' : 'ยกเลิกบันทึก'}
              </button>
              <button type="button" disabled={attEditSaving} onClick={handleAttSave}
                className="flex-1 px-4 py-2.5 bg-gray-900 text-white rounded-xl text-[12px] font-semibold disabled:opacity-50">
                {attEditSaving ? 'กำลังบันทึก…' : 'บันทึกการแก้ไข'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editLog && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" onClick={() => setEditLog(null)}>
          <div className="w-full max-w-md bg-white rounded-t-2xl p-5 space-y-4 pb-8" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[13px] font-semibold text-gray-900">แก้ไขบันทึก</div>
                <div className="text-[11px] text-gray-400 mt-0.5">{formatDate(editLog.date)} · ส่งใหม่เพื่อรออนุมัติ</div>
              </div>
              <button onClick={() => setEditLog(null)} className="p-1 text-gray-400 hover:text-gray-700"><XCircle size={18}/></button>
            </div>
            <form onSubmit={handleEditSubmit} className="space-y-3">
              <div>
                <label className="block text-[10px] text-gray-400 mb-1">สถานที่</label>
                <div className="flex gap-2">
                  {['office','wfh','onsite'].map((loc) => (
                    <button key={loc} type="button"
                      onClick={() => setEditForm({ ...editForm, location: loc })}
                      style={editForm.location === loc ? { background: LOC_COLOR[loc].bg, borderColor: LOC_COLOR[loc].text, color: LOC_COLOR[loc].text } : {}}
                      className={`flex-1 py-2 rounded-xl text-[12px] border-2 font-medium transition-all ${editForm.location === loc ? '' : 'border-gray-100 text-gray-400 bg-gray-50'}`}>
                      {LOC_COLOR[loc].label}
                    </button>
                  ))}
                </div>
              </div>
              {editForm.location === 'onsite' && (
                <input placeholder="ระบุสถานที่ on-site..."
                  value={editForm.locationName}
                  onChange={e => setEditForm({ ...editForm, locationName: e.target.value })}
                  className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-gray-400"/>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] text-gray-400 mb-1">เวลาเข้างาน</label>
                  <input type="time" required value={editForm.checkInTime}
                    onChange={e => setEditForm({ ...editForm, checkInTime: e.target.value })}
                    className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-gray-400"/>
                </div>
                <div>
                  <label className="block text-[10px] text-gray-400 mb-1">เวลาออกงาน</label>
                  <input type="time" required value={editForm.checkOutTime}
                    onChange={e => setEditForm({ ...editForm, checkOutTime: e.target.value })}
                    className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-gray-400"/>
                </div>
              </div>
              <div>
                <label className="block text-[10px] text-gray-400 mb-1">หมายเหตุ (ถ้ามี)</label>
                <input placeholder="เช่น เหตุผลที่แก้ไข..."
                  value={editForm.note}
                  onChange={e => setEditForm({ ...editForm, note: e.target.value })}
                  className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-gray-400"/>
              </div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setEditLog(null)}
                  className="px-4 py-2.5 bg-gray-100 rounded-xl text-[12px] text-gray-600 font-medium">ยกเลิก</button>
                <button type="submit" disabled={editSubmitting}
                  className="flex-1 py-2.5 bg-orange-500 text-white rounded-xl text-[12px] font-semibold disabled:opacity-50">
                  {editSubmitting ? 'กำลังส่ง...' : 'ส่งใหม่ รออนุมัติ'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── voucher preview modal ─────────────────────────────────────────── */}
      {voucherPreviewData && (
        <VoucherPreviewModal
          data={voucherPreviewData}
          creating={voucherCreating}
          onClose={() => setVoucherPreviewData(null)}
          onConfirm={handleCreateVoucherConfirm}
        />
      )}

      {/* ── profile hero ────────────────────────────────────────────────── */}
      <div className="bg-[#111] px-5 pt-5 pb-0">
        <div className="flex items-center gap-3 pb-4">
          <div className="w-14 h-14 rounded-2xl bg-[#BFFF00]/20 border border-[#BFFF00]/30 flex items-center justify-center text-[#BFFF00] text-lg font-bold flex-shrink-0">
            {user.name.slice(0,2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white text-base font-semibold">{user.name}</div>
            <div className="text-gray-500 text-xs mt-0.5">@{user.id}</div>
          </div>
          <span className={`px-3 py-1 rounded-full text-[11px] font-semibold ${isAdmin() ? 'bg-[#BFFF00] text-black' : 'bg-white/10 text-gray-400'}`}>
            {isAdmin() ? 'Admin' : 'Staff'}
          </span>
        </div>

        {/* ── tabs ──────────────────────────────────────────────────────── */}
        <div className="flex gap-1 overflow-x-auto scrollbar-none -mx-1 px-1">
          {tabs.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-medium border-b-2 transition-all whitespace-nowrap ${
                tab === t
                  ? 'border-[#BFFF00] text-[#BFFF00]'
                  : 'border-transparent text-gray-500 hover:text-gray-300'
              }`}
            >
              {TAB_LABELS[t]}
              {t === 'approve' && (pendingLogs.length + pendingLeaves.length) > 0 && (
                <span className="bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                  {pendingLogs.length + pendingLeaves.length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── content ─────────────────────────────────────────────────────── */}
      <div className="px-4 pt-5 pb-8">

      {/* ════════════════════════════════════════════════════════════════════
          TAB: ATTENDANCE (my own)
      ════════════════════════════════════════════════════════════════════ */}
      {tab === 'attendance' && (
        <div className="space-y-3">

          {/* ── check-in card ── */}
          <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
            {/* top: date + status */}
            <div className="flex items-start justify-between px-4 pt-4 pb-3 border-b border-gray-100 gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">วันนี้</div>
                <div className="text-[15px] font-semibold text-gray-900 mt-0.5 truncate">
                  {new Date().toLocaleDateString('en-GB', { weekday:'short', day:'numeric', month:'short' })}
                </div>
              </div>
              <div className="flex-shrink-0 text-right">
                {!todayLog ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-100 text-gray-500 text-[11px] font-medium whitespace-nowrap">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 inline-block"/>
                    ยังไม่เช็คอิน
                  </span>
                ) : todayLog.checkOut ? (
                  <div className="space-y-1 text-right">
                    <div className="text-[11px] text-gray-600 font-medium whitespace-nowrap">
                      {formatTimestamp(todayLog.checkIn)} – {formatTimestamp(todayLog.checkOut)}
                      {todayLog.totalHours && <span className="ml-1 text-gray-400">({todayLog.totalHours}h)</span>}
                    </div>
                    <Badge status={todayLog.status}/>
                  </div>
                ) : (
                  <div className="space-y-1 text-right">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-[11px] font-medium whitespace-nowrap">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block animate-pulse"/>
                      {formatTimestamp(todayLog.checkIn)}
                    </span>
                    <div><Badge status={todayLog.status}/></div>
                  </div>
                )}
              </div>
            </div>

            {/* middle: location selector (only before check-in) */}
            {!todayLog && (
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="text-[10px] text-gray-400 mb-2 font-medium uppercase tracking-wider">สถานที่ทำงาน</div>
                <div className="flex gap-2">
                  {['office','wfh','onsite'].map((loc) => (
                    <button
                      key={loc}
                      onClick={() => setLocChoice(loc)}
                      style={locChoice === loc ? { background: LOC_COLOR[loc].bg, borderColor: LOC_COLOR[loc].text, color: LOC_COLOR[loc].text } : {}}
                      className={`flex-1 py-2 rounded-xl text-[12px] border-2 font-medium transition-all ${
                        locChoice === loc ? '' : 'border-gray-100 text-gray-400 bg-gray-50'
                      }`}
                    >
                      {LOC_COLOR[loc].label}
                    </button>
                  ))}
                </div>
                {locChoice === 'onsite' && (
                  <input
                    value={onsiteName}
                    onChange={(e) => setOnsiteName(e.target.value)}
                    placeholder="ระบุสถานที่..."
                    className="mt-2 w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-gray-400"
                  />
                )}
              </div>
            )}


            {/* bottom: action button */}
            <div className="px-4 py-3">
              {!todayLog ? (
                <button onClick={handleCheckIn} disabled={ciLoading}
                  className="w-full py-3 rounded-xl bg-[#111] text-white text-[13px] font-semibold disabled:opacity-50 active:scale-95 transition-transform">
                  {ciLoading ? 'กำลังบันทึก...' : '⏱ เช็คอิน'}
                </button>
              ) : !todayLog.checkOut ? (
                <button onClick={handleCheckOut} disabled={ciLoading}
                  className="w-full py-3 rounded-xl bg-red-500 text-white text-[13px] font-semibold disabled:opacity-50 active:scale-95 transition-transform">
                  {ciLoading ? 'กำลังบันทึก...' : <><Flag size={13} className="inline mr-1"/>เช็คเอาท์</>}
                </button>
              ) : (
                <div className="text-center text-[12px] text-gray-400 py-1 flex items-center justify-center gap-1"><CheckCircle2 size={13}/>บันทึกวันนี้แล้ว</div>
              )}
            </div>
          </div>

          {/* retro entry */}
          <div>
            <button
              onClick={() => setShowRetroForm(!showRetroForm)}
              className="flex items-center gap-1.5 text-[11px] text-gray-500 hover:text-gray-900 transition-colors"
            >
              <Plus size={13}/> บันทึกย้อนหลัง
              <ChevronDown size={12} className={`transition-transform ${showRetroForm ? 'rotate-180' : ''}`}/>
            </button>

            {showRetroForm && (
              <SectionCard className="mt-2">
                <div className="text-[12px] font-semibold text-gray-900 mb-3">บันทึกเวลาย้อนหลัง</div>
                <form onSubmit={handleRetroSubmit} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] text-gray-400 mb-1">วันที่</label>
                      <input type="date" max={today} required
                        value={retroForm.date}
                        onChange={(e) => setRetroForm({ ...retroForm, date: e.target.value })}
                        className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-400 mb-1">สถานที่</label>
                      <div className="flex gap-1">
                        {['office','wfh','onsite'].map((loc) => (
                          <button key={loc} type="button"
                            onClick={() => setRetroForm({ ...retroForm, location: loc })}
                            style={retroForm.location === loc ? { background: LOC_COLOR[loc].bg, borderColor: LOC_COLOR[loc].text, color: LOC_COLOR[loc].text } : {}}
                            className={`flex-1 py-1.5 rounded-lg text-[10px] border transition-all ${retroForm.location === loc ? 'font-medium' : 'border-gray-200 text-gray-400 bg-white'}`}
                          >
                            {LOC_COLOR[loc].label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] text-gray-400 mb-1">เวลาเข้างาน</label>
                      <input type="time" required
                        value={retroForm.checkInTime}
                        onChange={(e) => setRetroForm({ ...retroForm, checkInTime: e.target.value })}
                        className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-400 mb-1">เวลาออกงาน</label>
                      <input type="time" required
                        value={retroForm.checkOutTime}
                        onChange={(e) => setRetroForm({ ...retroForm, checkOutTime: e.target.value })}
                        className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-gray-400"
                      />
                    </div>
                  </div>
                  {retroForm.location === 'onsite' && (
                    <input placeholder="ระบุสถานที่ on-site..."
                      value={retroForm.locationName}
                      onChange={(e) => setRetroForm({ ...retroForm, locationName: e.target.value })}
                      className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-gray-400"
                    />
                  )}
                  <div>
                    <label className="block text-[10px] text-gray-400 mb-1">หมายเหตุ</label>
                    <input placeholder="(ถ้ามี)"
                      value={retroForm.note}
                      onChange={(e) => setRetroForm({ ...retroForm, note: e.target.value })}
                      className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-gray-400"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setShowRetroForm(false)}
                      className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-[12px] text-gray-600">ยกเลิก</button>
                    <button type="submit" disabled={retroSubmitting}
                      className="flex-1 py-2 bg-gray-900 text-white rounded-lg text-[12px] font-medium disabled:opacity-50">
                      {retroSubmitting ? 'กำลังส่ง...' : 'ส่งรออนุมัติ'}
                    </button>
                  </div>
                </form>
              </SectionCard>
            )}
          </div>

          {/* stats */}
          {(() => {
            const totalHours = approvedLogs.reduce((s,l) => s+(l.totalHours||0), 0);
            const pendingCount = pendingToday + logs.filter(l=>l.status==='pending'&&l.date!==today).length;
            const todayStatus = !todayLog ? '—' : todayLog.status === 'approved' ? 'อนุมัติ' : 'รอ';
            return (
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
                  <div className="text-[10px] text-gray-400 uppercase tracking-wider font-medium mb-2">วันทำงาน</div>
                  <div className="text-3xl font-bold text-gray-900">{totalApproved}</div>
                  <div className="text-[11px] text-gray-400 mt-1">30 วันล่าสุด</div>
                </div>
                <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
                  <div className="text-[10px] text-gray-400 uppercase tracking-wider font-medium mb-2">ชั่วโมงรวม</div>
                  <div className="text-3xl font-bold text-gray-900">{totalHours.toFixed(1)}<span className="text-lg font-normal text-gray-400 ml-1">h</span></div>
                  <div className="text-[11px] text-gray-400 mt-1">เฉลี่ย {totalApproved ? (totalHours/totalApproved).toFixed(1) : 0}h/วัน</div>
                </div>
                <div className={`rounded-2xl p-4 shadow-sm ${pendingCount > 0 ? 'bg-amber-50 border border-amber-100' : 'bg-white border border-gray-100'}`}>
                  <div className="text-[10px] text-gray-400 uppercase tracking-wider font-medium mb-2">รออนุมัติ</div>
                  <div className={`text-3xl font-bold ${pendingCount > 0 ? 'text-amber-500' : 'text-gray-900'}`}>{pendingCount}</div>
                  <div className="text-[11px] text-gray-400 mt-1">รายการ</div>
                </div>
                <div className="bg-[#111] rounded-2xl p-4 shadow-sm">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider font-medium mb-2">วันนี้</div>
                  {!todayLog ? (
                    <>
                      <div className="text-[13px] font-semibold text-gray-500 mt-1">ยังไม่เช็คอิน</div>
                      <div className="text-[11px] text-gray-700 mt-1">—</div>
                    </>
                  ) : (
                    <>
                      <div className={`text-xl font-bold mt-1 ${todayLog.status === 'approved' ? 'text-[#BFFF00]' : 'text-amber-400'}`}>
                        {todayLog.status === 'approved' ? 'อนุมัติ' : 'รออนุมัติ'}
                      </div>
                      <div className="text-[11px] text-gray-600 mt-1">{todayLog.checkOut ? 'เช็คเอาท์แล้ว' : 'กำลังทำงาน'}</div>
                    </>
                  )}
                </div>
              </div>
            );
          })()}

          {/* location bar */}
          {totalApproved > 0 && (
            <div className="border border-gray-100 rounded-xl p-4">
              <div className="flex justify-between text-[11px] text-gray-400 mb-2">
                <span>Work Location (อนุมัติแล้ว)</span>
                <span className="font-medium text-gray-900">{totalApproved} วัน</span>
              </div>
              <div className="flex h-5 rounded-lg overflow-hidden gap-0.5">
                {['office','wfh','onsite'].map((loc) => {
                  const pct = totalApproved ? Math.round(locCounts[loc]/totalApproved*100) : 0;
                  if (!pct) return null;
                  return (
                    <div key={loc} style={{ width:`${pct}%`, background: LOC_COLOR[loc].bg, color: LOC_COLOR[loc].text }}
                      className="flex items-center justify-center text-[9px] font-semibold">
                      {pct}%
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-4 mt-2">
                {['office','wfh','onsite'].map((loc) => (
                  <div key={loc} className="flex items-center gap-1.5 text-[11px] text-gray-500">
                    <div className="w-2.5 h-2.5 rounded-sm" style={{ background: LOC_COLOR[loc].bg, border:`1px solid ${LOC_COLOR[loc].text}` }}/>
                    {LOC_COLOR[loc].label} <strong className="text-gray-900">{locCounts[loc]}</strong>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* leave balance */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[12px] font-semibold text-gray-900">วันลาคงเหลือ</span>
              <button onClick={() => setShowLeaveForm(!showLeaveForm)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#d4f542] text-gray-900 text-[11px] font-semibold">
                <Plus size={12}/> ยื่นใบลา
              </button>
            </div>
            {(() => {
              const quota = empInfo.startDate ? computeLeaveQuota(empInfo.startDate, empInfo.probationMonths) : null;
              const COLORS = { 'ลาพักร้อน':'#9b82d4', 'ลาป่วย':'#6aabdf', 'ลากิจ':'#5dbc78' };
              const BASE   = { 'ลาพักร้อน':10, 'ลาป่วย':30, 'ลากิจ':5 };
              return (
                <>
                  {quota ? (
                    <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mb-2 text-[10px] text-gray-400">
                      <span>รอบปีลา {formatDate(quota.cycleStartISO)} – {formatDate(quota.cycleEndISO)}</span>
                      {quota.inProbation && (
                        <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">
                          ทดลองงานถึง {formatDate(quota.probationEndISO)}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="mb-2 text-[10px] text-amber-600">ยังไม่ได้ตั้งวันเริ่มงาน — แอดมินตั้งค่าได้ที่หน้า Team เพื่อผูกโควต้ากับอายุงาน</div>
                  )}
                  <div className="grid grid-cols-3 gap-2">
                    {['ลาพักร้อน','ลาป่วย','ลากิจ'].map((name) => {
                      const total = quota ? quota.types[name].total : BASE[name];
                      const lockedUntil = quota ? quota.types[name].lockedUntil : null;
                      const used = quota
                        ? usedInCycle(staffLeaves, name, quota.cycleStartISO, quota.cycleEndISO)
                        : staffLeaves.filter(l => l.type === name && (l.status === 'approved' || l.status === 'pending')).reduce((s, l) => s + (l.days || 0), 0);
                      const remaining = Math.max(0, total - used);
                      const pct = total > 0 ? remaining / total * 100 : 0;
                      return (
                        <SectionCard key={name}>
                          <div className="flex justify-between text-[11px] mb-1.5">
                            <span className="text-gray-500">{name}</span>
                            <span className="font-medium text-gray-900">{remaining}/{total} วัน</span>
                          </div>
                          <div className="h-1 bg-gray-200 rounded-full">
                            <div className="h-full rounded-full" style={{ width:`${pct}%`, background:COLORS[name] }}/>
                          </div>
                          {lockedUntil && <div className="mt-1 text-[9px] text-amber-600">ได้สิทธิ์ {BASE[name]} วันเมื่อผ่านโปรฯ</div>}
                        </SectionCard>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </div>

          {/* leave form */}
          {showLeaveForm && (
            <SectionCard>
              <div className="text-[12px] font-semibold text-gray-900 mb-3">ยื่นใบลา</div>
              <form onSubmit={handleLeaveSubmit} className="space-y-3">
                <div>
                  <label className="block text-[10px] text-gray-400 mb-1">ประเภทการลา</label>
                  <select value={leaveForm.type} onChange={e=>setLeaveForm({...leaveForm,type:e.target.value})}
                    className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none">
                    {LEAVE_TYPES.map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] text-gray-400 mb-1">วันเริ่มต้น</label>
                    <input type="date" value={leaveForm.startDate} onChange={e=>{
                      const start = e.target.value;
                      const end = leaveForm.endDate < start ? start : leaveForm.endDate;
                      const d = Math.round((new Date(end) - new Date(start)) / 86400000) + 1;
                      setLeaveForm({...leaveForm, startDate: start, endDate: end, days: d});
                    }} className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none"/>
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-400 mb-1">วันสิ้นสุด</label>
                    <input type="date" value={leaveForm.endDate} min={leaveForm.startDate} onChange={e=>{
                      const end = e.target.value;
                      const d = Math.round((new Date(end) - new Date(leaveForm.startDate)) / 86400000) + 1;
                      setLeaveForm({...leaveForm, endDate: end, days: Math.max(1, d)});
                    }} className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none"/>
                  </div>
                </div>
                <div className="text-[11px] text-gray-500">
                  รวม <span className="font-semibold text-gray-900">{leaveForm.days} วัน</span>
                </div>
                <div>
                  <label className="block text-[10px] text-gray-400 mb-1">เหตุผล</label>
                  <textarea value={leaveForm.reason} onChange={e=>setLeaveForm({...leaveForm,reason:e.target.value})}
                    rows={2} placeholder="เหตุผลการลา..."
                    className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none resize-none"/>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={()=>setShowLeaveForm(false)}
                    className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-[12px] text-gray-600">ยกเลิก</button>
                  <button type="submit" disabled={leaveSubmitting}
                    className="flex-1 py-2 bg-gray-900 text-white rounded-lg text-[12px] font-medium disabled:opacity-50">
                    {leaveSubmitting ? 'กำลังส่ง...' : 'ส่งคำขอลา'}
                  </button>
                </div>
              </form>
            </SectionCard>
          )}

          {/* daily log table */}
          <div className="border border-gray-100 rounded-xl overflow-hidden">
            <div className="flex justify-between items-center px-4 py-3 bg-[#fafafa] border-b border-gray-100">
              <span className="text-[12px] font-medium text-gray-900">บันทึกรายวัน (30 วันล่าสุด)</span>
              <span className="text-[10px] text-gray-400">* เฉพาะที่อนุมัติแล้วนับใน metrics</span>
            </div>
            {logsLoading ? (
              <div className="text-center py-8 text-[12px] text-gray-400">กำลังโหลด...</div>
            ) : logs.length === 0 ? (
              <div className="text-center py-8 text-[12px] text-gray-400">ยังไม่มีบันทึก</div>
            ) : (
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="bg-[#fafafa] border-b border-gray-100">
                    {['วันที่','สถานที่','เข้างาน','ออกงาน','ชั่วโมง','ค่าใช้จ่าย','สถานะ'].map(h=>(
                      <th key={h} className="px-3 py-2 text-left text-[10px] text-gray-400 font-normal">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-3 py-2.5 text-gray-600">{formatDate(l.date)}</td>
                      <td className="px-3 py-2.5"><LocBadge loc={l.location}/></td>
                      <td className="px-3 py-2.5 text-gray-600">{formatTimestamp(l.checkIn)}</td>
                      <td className="px-3 py-2.5 text-gray-600">{l.checkOut ? formatTimestamp(l.checkOut) : '-'}</td>
                      <td className="px-3 py-2.5 text-gray-400">{l.totalHours ? `${l.totalHours}h` : '-'}</td>
                      <td className="px-3 py-2.5 text-gray-600">
                        {(() => {
                          const travel = l.travelCost || 0;
                          const field  = l.fieldAllowance || 0;
                          const other  = (l.dailyExpenses || []).reduce((s, e) => s + (Number(e.amount) || 0), 0);
                          const total  = travel + field + other;
                          if (!total) return '-';
                          return (
                            <span title={[travel && `เดินทาง ฿${travel}`, field && `เบี้ยเลี้ยง ฿${field}`, ...((l.dailyExpenses||[]).map(e=>`${e.label} ฿${e.amount}`))].filter(Boolean).join(' · ')}>
                              ฿{total.toLocaleString()}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <Badge status={l.status}/>
                          {l.status === 'rejected' && (
                            <button onClick={() => openEditLog(l)}
                              className="px-2 py-0.5 rounded text-[10px] font-medium bg-orange-50 text-orange-600 border border-orange-200 hover:bg-orange-100 transition-colors whitespace-nowrap">
                              แก้ไข
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* leave history table */}
          <div className="border border-gray-100 rounded-xl overflow-hidden">
            <div className="flex justify-between items-center px-4 py-3 bg-[#fafafa] border-b border-gray-100">
              <span className="text-[12px] font-medium text-gray-900">ประวัติการลา</span>
              <span className="text-[10px] text-gray-400">{staffLeaves.length} รายการ</span>
            </div>
            {staffLeaves.length === 0 ? (
              <div className="text-center py-8 text-[12px] text-gray-400">ยังไม่มีประวัติการลา</div>
            ) : (
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="bg-[#fafafa] border-b border-gray-100">
                    {['วันที่','ประเภท','จำนวน','สถานะ','เหตุผล'].map(h=>(
                      <th key={h} className="px-3 py-2 text-left text-[10px] text-gray-400 font-normal">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...staffLeaves].sort((a,b)=>(b.startDate||'').localeCompare(a.startDate||'')).map((l) => {
                    const LEAVE_COLOR = { 'ลาพักร้อน':'#818cf8', 'ลาป่วย':'#f59e0b', 'ลากิจ':'#10b981', 'หยุดชดเชย':'#06b6d4' };
                    const ST = {
                      approved:  { bg:'#eff6ff', tx:'#1d4ed8', label:'อนุมัติ' },
                      pending:   { bg:'#fefce8', tx:'#a16207', label:'รออนุมัติ' },
                      rejected:  { bg:'#fff1f2', tx:'#dc2626', label:'ไม่อนุมัติ' },
                      cancelled: { bg:'#f3f4f6', tx:'#6b7280', label:'ยกเลิก' },
                    };
                    const st  = ST[l.status] || ST.pending;
                    const dot = LEAVE_COLOR[l.type] || '#9ca3af';
                    return (
                      <tr key={l.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
                          {l.startDate === l.endDate ? formatDate(l.startDate) : `${formatDate(l.startDate)} – ${formatDate(l.endDate)}`}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ background:`${dot}22`, color:dot }}>{l.type}</span>
                        </td>
                        <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{l.days} วัน</td>
                        <td className="px-3 py-2.5">
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ background:st.bg, color:st.tx }}>{st.label}</span>
                        </td>
                        <td className="px-3 py-2.5 text-gray-500 max-w-[160px] truncate" title={l.reason||''}>{l.reason || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          TAB: APPROVE (admin only)
      ════════════════════════════════════════════════════════════════════ */}
      {tab === 'approve' && isAdmin() && (
        <div className="space-y-4">
          {pendingLogs.length > 0 && (
            <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <div className="flex items-center gap-2 text-[13px] text-amber-700">
                <AlertCircle size={16}/>
                <strong>{pendingLogs.length} รายการ</strong> รออนุมัติ
              </div>
              <button onClick={handleApproveAll}
                className="px-4 py-1.5 bg-[#d4f542] rounded-lg text-[11px] font-semibold text-gray-900">
                อนุมัติทั้งหมด
              </button>
            </div>
          )}

          {pendingLoading ? (
            <div className="text-center py-10 text-[12px] text-gray-400">กำลังโหลด...</div>
          ) : pendingLogs.length === 0 && pendingLeaves.length === 0 ? (
            <div className="text-center py-10 text-[12px] text-gray-400">
              <CheckCircle2 size={32} className="mx-auto mb-2 text-green-400"/>
              ไม่มีรายการรออนุมัติ
            </div>
          ) : pendingLogs.length === 0 ? null : (
            <div className="space-y-2">
              {pendingLogs.map((l) => {
                const edits = allowanceEdits[l.id] || {};
                const tc = edits.travelCost    !== undefined ? edits.travelCost    : (l.travelCost    ?? '');
                const fa = edits.fieldAllowance !== undefined ? edits.fieldAllowance : (l.fieldAllowance ?? '');
                const setEdit = (k, v) => setAllowanceEdits(prev => ({ ...prev, [l.id]: { ...(prev[l.id]||{}), [k]: v } }));
                return (
                  <div key={l.id} className="bg-[#f8f8f8] rounded-xl px-4 py-3 space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-white text-[10px] font-semibold flex-shrink-0">
                        {l.userName?.slice(0,2).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="text-[12px] font-medium text-gray-900">{l.userName}</div>
                        <div className="flex items-center gap-2 text-[10px] text-gray-400 mt-0.5">
                          <span>{formatDate(l.date)}</span>
                          <LocBadge loc={l.location}/>
                          <span>{formatTimestamp(l.checkIn)}{l.checkOut ? ` → ${formatTimestamp(l.checkOut)}` : ' → ปัจจุบัน'}</span>
                          {l.totalHours && <span className="font-medium text-gray-700">{l.totalHours}h</span>}
                          {l.isRetro && <span className="px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded text-[9px]">ย้อนหลัง</span>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => handleApprove(l.id, l.userId)}
                          className="px-3 py-1.5 bg-[#d4f542] rounded-lg text-[11px] font-semibold text-gray-900">
                          อนุมัติ
                        </button>
                        <button onClick={() => handleReject(l.id, l.userId)}
                          className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-[11px] text-gray-500 hover:border-red-200 hover:text-red-500">
                          ปฏิเสธ
                        </button>
                      </div>
                    </div>
                    {/* allowance row */}
                    <div className="pt-1 border-t border-gray-200 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-gray-400 w-20 flex-shrink-0">ค่าเดินทาง</span>
                        <div className="relative flex-1">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">฿</span>
                          <input type="number" min="0" placeholder="0" value={tc}
                            onChange={(e) => setEdit('travelCost', e.target.value)}
                            className="w-full bg-white border border-gray-200 rounded-lg pl-5 pr-2 py-1.5 text-[11px] text-gray-900 outline-none focus:border-gray-400"
                          />
                        </div>
                        <span className="text-[10px] text-gray-400 w-20 flex-shrink-0">เบี้ยเลี้ยง</span>
                        <div className="relative flex-1">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">฿</span>
                          <input type="number" min="0" placeholder="0" value={fa}
                            onChange={(e) => setEdit('fieldAllowance', e.target.value)}
                            className="w-full bg-white border border-gray-200 rounded-lg pl-5 pr-2 py-1.5 text-[11px] text-gray-900 outline-none focus:border-gray-400"
                          />
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] text-gray-400 mb-1">ค่าใช้จ่ายอื่นๆ</div>
                        <DailyExpenseEditor
                          items={edits.dailyExpenses ?? (l.dailyExpenses || [])}
                          onChange={(v) => setEdit('dailyExpenses', v)}
                        />
                      </div>
                      <button onClick={() => handleSaveAllowance(l.id)}
                        className="w-full py-1.5 bg-gray-900 text-white rounded-lg text-[10px] font-medium">
                        บันทึก
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── leave requests section ── */}
          {pendingLeaves.length > 0 && (
            <div className="mt-4 space-y-2">
              <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide pb-1 border-b border-gray-100">
                คำขอลา ({pendingLeaves.length})
              </div>
              {pendingLeaves.map((l) => {
                const LEAVE_COLOR = { 'ลาพักร้อน': '#818cf8', 'ลาป่วย': '#f59e0b', 'ลากิจ': '#10b981' };
                const LEAVE_EN    = { 'ลาพักร้อน': 'Annual',   'ลาป่วย': 'Sick',    'ลากิจ': 'Personal' };
                const dotColor = LEAVE_COLOR[l.type] || '#9ca3af';
                return (
                  <div key={l.id} className="bg-[#f8f8f8] rounded-xl px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 text-[10px] font-semibold flex-shrink-0">
                        {l.userName?.slice(0,2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[12px] font-medium text-gray-900">{l.userName}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ background: `${dotColor}22`, color: dotColor }}>
                            {LEAVE_EN[l.type] || l.type}
                          </span>
                        </div>
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          {l.startDate === l.endDate ? formatDate(l.startDate) : `${formatDate(l.startDate)} – ${formatDate(l.endDate)}`}
                          {' · '}{l.days} วัน
                          {l.reason && <span className="ml-1 text-gray-500">"{l.reason}"</span>}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <button onClick={() => handleLeaveApprove(l.id, l.userId)}
                          className="px-3 py-1.5 bg-[#d4f542] rounded-lg text-[11px] font-semibold text-gray-900">
                          อนุมัติ
                        </button>
                        <button onClick={() => handleLeaveReject(l.id, l.userId)}
                          className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-[11px] text-gray-500 hover:border-red-200 hover:text-red-500">
                          ปฏิเสธ
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════
          TAB: ATTENDANCE SUMMARY (admin — per-person monthly)
      ════════════════════════════════════════════════════════════════════ */}
      {tab === 'att_summary' && isAdmin() && (() => {
        const ym = attMonth;
        const [yy, mm] = ym.split('-').map(Number);
        const daysInMonth = new Date(yy, mm, 0).getDate();
        const PILL = {
          office: { bg:'#efe9fb', tx:'#6b4fa8', label:'Office' },
          wfh:    { bg:'#e4f0fb', tx:'#2f6ea5', label:'WFH' },
          onsite: { bg:'#e6f7ea', tx:'#2f8a52', label:'On-site' },
          leave:  { bg:'#fdf1d9', tx:'#92600b', label:'ลา' },
          off:    { bg:'#f3f4f6', tx:'#b0b3b8', label:'—' },
        };
        const nameOf = (uid) => (ALL_USERS.find(u => u.id === uid)?.name) || uid;

        // นับเฉพาะ approved; pending แสดงเป็นตัวเตือน
        const monthLogs      = allLogs.filter(l => (l.date||'').startsWith(ym) && l.status === 'approved');
        const pendingLogCnt  = allLogs.filter(l => (l.date||'').startsWith(ym) && l.status === 'pending').length;

        // วันลาที่ตกในเดือนนี้ (นับรายวันจากช่วงจริง ไม่ใช่ช่อง days)
        const monthStart = `${ym}-01`;
        const monthEnd   = `${ym}-${String(daysInMonth).padStart(2,'0')}`;
        const enumDays = (a, b) => {
          const out = []; const end = new Date(b + 'T12:00:00');
          for (let d = new Date(a + 'T12:00:00'); d <= end; d.setDate(d.getDate()+1)) {
            out.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
          }
          return out;
        };
        const leaveDatesByUser = {}; // uid -> { ds: leaveRecord }
        let pendingLeaveCnt = 0;
        for (const lv of allLeaves) {
          if (!lv.startDate || !lv.endDate) continue;
          const s = lv.startDate > monthStart ? lv.startDate : monthStart;
          const e = lv.endDate   < monthEnd   ? lv.endDate   : monthEnd;
          if (s > e) continue; // ไม่คร่อมเดือนนี้
          if (lv.status === 'pending') { pendingLeaveCnt += 1; continue; }
          if (lv.status !== 'approved') continue;
          (leaveDatesByUser[lv.userId] ||= {});
          enumDays(s, e).forEach(ds => { leaveDatesByUser[lv.userId][ds] = lv; });
        }

        // รวมรายคน (เก็บ record อ้างอิงไว้ด้วย เพื่อกดแก้/ยกเลิกจากปฏิทิน)
        const byPerson = {};
        const ensure = (uid) => (byPerson[uid] ||= { id: uid, name: nameOf(uid), work:0, office:0, wfh:0, onsite:0, leave:0, dates:{}, logByDate:{}, leaveByDate:{} });
        monthLogs.forEach(l => {
          const p = ensure(l.userId);
          const loc = ['office','wfh','onsite'].includes(l.location) ? l.location : 'onsite';
          p[loc] += 1; p.work += 1; p.dates[l.date] = loc; p.logByDate[l.date] = l;
        });
        Object.entries(leaveDatesByUser).forEach(([uid, map]) => {
          const p = ensure(uid);
          Object.entries(map).forEach(([ds, lv]) => { p.leave += 1; p.leaveByDate[ds] = lv; if (!p.dates[ds]) p.dates[ds] = 'leave'; });
        });
        const rows = Object.values(byPerson).sort((a,b) => b.work - a.work || b.leave - a.leave || a.name.localeCompare(b.name));
        const teamWork  = rows.reduce((s,p) => s + p.work, 0);
        const teamLeave = rows.reduce((s,p) => s + p.leave, 0);

        const numCell = (n) => n > 0
          ? <span className="text-[13px] font-semibold text-gray-900">{n}</span>
          : <span className="text-[13px] text-gray-300">0</span>;
        const pill = (n, kind) => n > 0
          ? <span style={{ background: PILL[kind].bg, color: PILL[kind].tx }} className="inline-block min-w-[24px] px-1.5 py-0.5 rounded-full text-[12px] font-semibold">{n}</span>
          : <span className="text-[12px] text-gray-300">–</span>;

        return (
          <div className="space-y-4">
            {/* month selector */}
            <div className="flex items-center justify-between">
              <button onClick={() => setAttMonth(prevMonth(attMonth))}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100">
                <ChevronLeft size={16} className="text-gray-500"/>
              </button>
              <span className="text-[13px] font-semibold text-gray-900">{fmtMonthEn(attMonth)}</span>
              <button onClick={() => setAttMonth(nextMonth(attMonth))}
                disabled={attMonth >= today.slice(0,7)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 disabled:opacity-30">
                <ChevronRight size={16} className="text-gray-500"/>
              </button>
            </div>

            {/* team summary */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-[#f8f8f8] rounded-xl p-3">
                <div className="text-[9px] text-gray-400">วันทำงานรวมทั้งทีม</div>
                <div className="text-[16px] font-bold text-gray-900">{teamWork} <span className="text-[10px] font-normal text-gray-400">วัน</span></div>
                <div className="text-[9px] text-gray-400">{rows.length} คน</div>
              </div>
              <div className="bg-[#fefce8] rounded-xl p-3">
                <div className="text-[9px] text-gray-400">วันลารวมทั้งทีม</div>
                <div className="text-[16px] font-bold" style={{ color:'#a16207' }}>{teamLeave} <span className="text-[10px] font-normal text-gray-400">วัน</span></div>
                <div className="text-[9px] text-gray-400">เฉพาะวันที่ตกในเดือนนี้</div>
              </div>
            </div>

            {/* pending warning */}
            {(pendingLogCnt + pendingLeaveCnt) > 0 && (
              <div className="flex items-start gap-2 bg-[#fff7ed] border border-[#fed7aa] rounded-xl px-3 py-2">
                <AlertCircle size={14} className="text-[#c2610c] flex-shrink-0 mt-0.5"/>
                <div className="text-[11px] text-[#9a3412]">
                  ยังมีรายการรออนุมัติเดือนนี้ที่ยังไม่ถูกนับ:
                  {pendingLogCnt > 0 && <> เวลาทำงาน {pendingLogCnt} รายการ</>}
                  {pendingLogCnt > 0 && pendingLeaveCnt > 0 && ' ·'}
                  {pendingLeaveCnt > 0 && <> ใบลา {pendingLeaveCnt} รายการ</>}
                  {' — '}อนุมัติที่แท็บ “อนุมัติ” ก่อนเพื่อให้ยอดครบ
                </div>
              </div>
            )}

            {/* per-person table (scrolls on narrow screens) */}
            {attSumLoading ? (
              <div className="text-center text-[12px] text-gray-400 py-8">กำลังโหลด…</div>
            ) : rows.length === 0 ? (
              <div className="text-center text-[12px] text-gray-400 py-8">ไม่มีข้อมูลการเข้างานในเดือนนี้</div>
            ) : (
              <div className="table-scroll bg-[#f8f8f8] rounded-xl overflow-hidden">
                <div className="min-w-[320px]">
                  {/* header */}
                  <div className="grid items-center px-3 py-2 border-b border-gray-200 text-[10px] font-semibold text-gray-500"
                       style={{ gridTemplateColumns:'1fr 40px 40px 38px 46px 34px' }}>
                    <span>พนักงาน</span>
                    <span className="text-right">ทำงาน</span>
                    <span className="text-right">Office</span>
                    <span className="text-right">WFH</span>
                    <span className="text-right">On-site</span>
                    <span className="text-right">ลา</span>
                  </div>
                  {rows.map(p => {
                    const active = attPerson === p.id;
                    return (
                      <div key={p.id} className="border-b border-gray-100 last:border-b-0">
                        <button type="button" onClick={() => setAttPerson(active ? null : p.id)}
                          className={`w-full grid items-center px-3 py-2.5 text-left transition-colors ${active ? 'bg-[#eef7c4]' : 'hover:bg-gray-100'}`}
                          style={{ gridTemplateColumns:'1fr 40px 40px 38px 46px 34px' }}>
                          <span className="flex items-center gap-2 min-w-0">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${active ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-600'}`}>
                              {p.name?.[0]?.toUpperCase()}
                            </span>
                            <span className="text-[12px] font-medium text-gray-900 truncate">{p.name}</span>
                          </span>
                          <span className="text-right">{numCell(p.work)}</span>
                          <span className="text-right">{pill(p.office, 'office')}</span>
                          <span className="text-right">{pill(p.wfh, 'wfh')}</span>
                          <span className="text-right">{pill(p.onsite, 'onsite')}</span>
                          <span className="text-right">{pill(p.leave, 'leave')}</span>
                        </button>
                        {active && (
                          <div className="px-3 pb-3 pt-1 bg-white">
                            <div className="text-[10px] font-semibold text-gray-500 mb-2">ปฏิทินรายวัน · {fmtMonthEn(attMonth)}</div>
                            <div className="flex flex-wrap gap-1">
                              {Array.from({ length: daysInMonth }, (_, i) => {
                                const day = i + 1;
                                const ds = `${ym}-${String(day).padStart(2,'0')}`;
                                const st = p.dates[ds] || 'off';
                                const rec = st === 'leave' ? p.leaveByDate[ds] : p.logByDate[ds];
                                const editable = rec && st !== 'off';
                                return (
                                  <button key={day} type="button" disabled={!editable}
                                    onClick={editable ? () => openAttEdit(st === 'leave' ? 'leave' : 'log', rec, p.name) : undefined}
                                    title={`${day} · ${PILL[st].label}${editable ? ' · แตะเพื่อแก้/ยกเลิก' : ''}`}
                                    style={{ background: PILL[st].bg, color: PILL[st].tx }}
                                    className={`w-[22px] h-[22px] rounded-[5px] flex items-center justify-center text-[9px] font-medium ${editable ? 'hover:ring-2 hover:ring-gray-400 cursor-pointer' : 'cursor-default'}`}>
                                    {day}
                                  </button>
                                );
                              })}
                            </div>
                            <div className="flex flex-wrap gap-3 mt-2.5 text-[10px] text-gray-500">
                              {['office','wfh','onsite','leave','off'].map(k => (
                                <span key={k} className="inline-flex items-center gap-1">
                                  <span style={{ background: PILL[k].bg }} className="w-2.5 h-2.5 rounded-[3px] inline-block"/>
                                  {k === 'off' ? 'หยุด/ไม่มีบันทึก' : PILL[k].label}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <p className="text-[10px] text-gray-400 px-1">แตะชื่อเพื่อดูปฏิทินรายวัน · แตะวันในปฏิทินเพื่อแก้/ยกเลิกรายการที่อนุมัติแล้ว · นับเฉพาะรายการที่อนุมัติแล้ว · วันลานับเฉพาะวันที่ตกในเดือนนี้</p>
          </div>
        );
      })()}

      {/* ════════════════════════════════════════════════════════════════════
          TAB: EXPENSES (staff — own claims)
      ════════════════════════════════════════════════════════════════════ */}
      {tab === 'expenses' && (() => {
        const monthExp = expenses.filter(e => (e.date || '').startsWith(expMonth));
        const mTotal   = monthExp.reduce((s, e) => s + Number(e.amount || 0), 0);
        const mPending = monthExp.filter(e => e.status === 'pending');
        const mApproved= monthExp.filter(e => e.status === 'approved');
        const mXfer    = monthExp.filter(e => e.status === 'transferred');
        return (
          <div className="space-y-4">
            {/* month selector */}
            <div className="flex items-center justify-between">
              <button onClick={() => setExpMonth(prevMonth(expMonth))}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100">
                <ChevronLeft size={16} className="text-gray-500"/>
              </button>
              <span className="text-[13px] font-semibold text-gray-900">{fmtMonthEn(expMonth)}</span>
              <button onClick={() => setExpMonth(nextMonth(expMonth))}
                disabled={expMonth >= today.slice(0,7)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 disabled:opacity-30">
                <ChevronRight size={16} className="text-gray-500"/>
              </button>
            </div>

            {/* monthly summary */}
            {monthExp.length > 0 && (
              <div className="bg-[#f8f8f8] rounded-xl p-4 space-y-3">
                <div className="flex items-baseline justify-between">
                  <span className="text-[11px] text-gray-400">ยอดรวมเดือนนี้</span>
                  <span className="text-[18px] font-bold text-gray-900">฿{mTotal.toLocaleString()}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  {[
                    { label: 'รออนุมัติ', count: mPending.length, amt: mPending.reduce((s,e)=>s+Number(e.amount||0),0), color: '#a16207', bg: '#fefce8' },
                    { label: 'รอโอน',     count: mApproved.length, amt: mApproved.reduce((s,e)=>s+Number(e.amount||0),0), color: '#1d4ed8', bg: '#eff6ff' },
                    { label: 'โอนแล้ว',   count: mXfer.length,    amt: mXfer.reduce((s,e)=>s+Number(e.amount||0),0),    color: '#16a34a', bg: '#f0fdf4' },
                  ].map(s => (
                    <div key={s.label} style={{ background: s.bg }} className="rounded-lg py-2 px-1">
                      <div style={{ color: s.color }} className="text-[9px] font-medium">{s.label}</div>
                      <div style={{ color: s.color }} className="text-[13px] font-bold">{s.count}</div>
                      <div style={{ color: s.color }} className="text-[9px] opacity-70">฿{s.amt.toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <span className="text-[12px] text-gray-500">{monthExp.length} รายการ</span>
              <button onClick={() => setShowExpForm(!showExpForm)}
                className="flex items-center gap-1.5 px-3 py-2 bg-[#d4f542] rounded-lg text-[11px] font-semibold text-gray-900">
                <Plus size={13}/> เพิ่มรายการ
              </button>
            </div>

            {user?.id !== 'admin' && showExpForm && <ExpenseForm
              form={expForm} setForm={setExpForm}
              receiptFile={receiptFile} setReceiptFile={setReceiptFile}
              fileRef={fileRef}
              onSubmit={handleExpSubmit}
              onCancel={() => { setShowExpForm(false); setReceiptFile(null); }}
              submitting={expSubmitting}
              cats={EXPENSE_CATS}
              descOptions={expSuggest.descriptions}
              projectOptions={expSuggest.projects}
            />}

            {expLoading ? (
              <div className="text-center py-8 text-[12px] text-gray-400">กำลังโหลด...</div>
            ) : monthExp.length === 0 && !showExpForm ? (
              <div className="text-center py-10 text-[12px] text-gray-400">
                <Receipt size={28} className="mx-auto mb-2 text-gray-300"/>
                ไม่มีรายการในเดือนนี้
              </div>
            ) : (
              <ExpenseList items={monthExp} showActions={false} onDelete={handleExpDelete} onUploadReceipt={handleReceiptUpload} onClearReceipt={handleReceiptClear} uploadingReceiptId={uploadingReceiptId}/>
            )}

            {/* ── ใบสำคัญจ่าย section ── */}
            {vouchers.length > 0 && (
              <div className="space-y-2 pt-2">
                <div className="flex items-center gap-2">
                  <FileText size={13} className="text-gray-400"/>
                  <span className="text-[12px] font-semibold text-gray-900">ใบสำคัญจ่าย</span>
                </div>
                {vouchers.map(v => (
                  <div key={v.id} className="bg-[#f8f8f8] rounded-xl p-4 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-[12px] font-semibold text-gray-900">{v.voucherNo}</div>
                        <div className="text-[10px] text-gray-400">{(v.items||[]).length} รายการ · ฿{Number(v.totalAmount).toLocaleString()} · {fmtMonthEn(v.month||'')}</div>
                      </div>
                      <span style={v.status==='signed'
                        ? {background:'#f0fdf4',color:'#16a34a'}
                        : {background:'#fef3c7',color:'#b45309'}}
                        className="text-[10px] font-medium px-2 py-0.5 rounded-full">
                        {v.status === 'signed' ? 'เซ็นแล้ว' : 'เอกสารรอเซ็นรับเงิน'}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => openVoucherPrint(v)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-[11px] text-gray-700 hover:bg-gray-50">
                        <Printer size={11}/> ดูเอกสาร
                      </button>
                      {v.status !== 'signed' && (
                        <button onClick={() => handleSignVoucher(v.id)}
                          className="flex-1 py-1.5 bg-gray-900 text-white rounded-lg text-[11px] font-semibold">
                          ยืนยันรับเงิน
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* ════════════════════════════════════════════════════════════════════
          TAB: EXPENSES ADMIN (admin — all claims)
      ════════════════════════════════════════════════════════════════════ */}
      {tab === 'expenses_admin' && isAdmin() && (() => {
        const monthAllRaw = allExpenses.filter(e => (e.date || '').startsWith(expMonth));
        // เลือกคนแล้ว → ทุกอย่างใต้ "ภาพรวมรายคน" จะเหลือเฉพาะคนนั้น (เดือนที่เลือก)
        const monthAll  = expPersonFilter ? monthAllRaw.filter(e => e.userId === expPersonFilter) : monthAllRaw;
        const filtered  = expStatusFilter === 'all' ? monthAll : monthAll.filter(e => e.status === expStatusFilter);
        const mPending  = monthAll.filter(e => e.status === 'pending');
        const mApproved = monthAll.filter(e => e.status === 'approved');
        const mXfer     = monthAll.filter(e => e.status === 'transferred');
        // การ์ดสรุปด้านบน = ภาพรวมทั้งเดือนของทุกคนเสมอ (ไม่ผูกกับคนที่เลือก)
        const sum        = (arr) => arr.reduce((s,e) => s + Number(e.amount||0), 0);
        const aPending   = monthAllRaw.filter(e => e.status === 'pending');
        const aApproved  = monthAllRaw.filter(e => e.status === 'approved');
        const aXfer      = monthAllRaw.filter(e => e.status === 'transferred');
        const mTotalAll  = sum(monthAllRaw);
        const mXferAmt   = sum(aXfer);

        // per-person summary — สรุปจากทุกคนเสมอ เพื่อให้สลับคนได้ตลอด
        const byPerson = {};
        monthAllRaw.forEach(e => {
          if (!byPerson[e.userId]) byPerson[e.userId] = { id: e.userId, name: e.userName, count: 0, total: 0, pending: 0, approved: 0, transferred: 0 };
          byPerson[e.userId].count += 1;
          byPerson[e.userId].total += Number(e.amount||0);
          if (e.status === 'pending') byPerson[e.userId].pending += Number(e.amount||0);
          else if (e.status === 'approved') byPerson[e.userId].approved += Number(e.amount||0);
          else if (e.status === 'transferred') byPerson[e.userId].transferred += Number(e.amount||0);
        });
        const personRows = Object.values(byPerson).sort((a,b) => b.total - a.total);
        const selPerson  = expPersonFilter ? byPerson[expPersonFilter] : null;

        // แยกตามหมวดหมู่ของคนที่เลือก (เดือนนี้)
        const byCat = {};
        if (selPerson) monthAll.forEach(e => {
          const c = e.category || 'อื่นๆ';
          if (!byCat[c]) byCat[c] = { cat: c, count: 0, total: 0 };
          byCat[c].count += 1;
          byCat[c].total += Number(e.amount||0);
        });
        const catRows = Object.values(byCat).sort((a,b) => b.total - a.total);
        const catMax  = catRows.length ? catRows[0].total : 0;

        return (
          <div className="space-y-4">
            {/* month selector */}
            <div className="flex items-center justify-between">
              <button onClick={() => setExpMonth(prevMonth(expMonth))}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100">
                <ChevronLeft size={16} className="text-gray-500"/>
              </button>
              <span className="text-[13px] font-semibold text-gray-900">{fmtMonthEn(expMonth)}</span>
              <button onClick={() => setExpMonth(nextMonth(expMonth))}
                disabled={expMonth >= today.slice(0,7)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 disabled:opacity-30">
                <ChevronRight size={16} className="text-gray-500"/>
              </button>
            </div>

            {/* summary stats */}
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'รวมทั้งเดือน', val: `฿${mTotalAll.toLocaleString()}`, sub: `${monthAllRaw.length} รายการ`, bg:'#f8f8f8', col:'#111' },
                { label: 'โอนคืนแล้ว',  val: `฿${mXferAmt.toLocaleString()}`, sub: `${aXfer.length} รายการ`, bg:'#f0fdf4', col:'#16a34a' },
                { label: 'รออนุมัติ',   val: `${aPending.length} รายการ`, sub: `฿${sum(aPending).toLocaleString()}`, bg:'#fefce8', col:'#a16207' },
                { label: 'อนุมัติ · รอโอน', val: `${aApproved.length} รายการ`, sub: `฿${sum(aApproved).toLocaleString()}`, bg:'#eff6ff', col:'#1d4ed8' },
              ].map(s => (
                <div key={s.label} style={{ background: s.bg }} className="rounded-xl p-3">
                  <div className="text-[9px] text-gray-400">{s.label}</div>
                  <div style={{ color: s.col }} className="text-[15px] font-bold">{s.val}</div>
                  <div className="text-[9px] text-gray-400">{s.sub}</div>
                </div>
              ))}
            </div>

            {/* per-person summary */}
            {personRows.length > 0 && (
              <div className="bg-[#f8f8f8] rounded-xl overflow-hidden">
                <div className="px-4 py-2.5 border-b border-gray-200 flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-gray-700">ภาพรวมรายคน</span>
                  <span className="text-[9px] text-gray-400">แตะชื่อเพื่อดูเฉพาะคนนั้น</span>
                </div>
                <div className="divide-y divide-gray-100">
                  {personRows.map(p => {
                    const active = expPersonFilter === p.id;
                    return (
                    <button key={p.id} type="button"
                      onClick={() => {
                        setExpPersonFilter(active ? null : p.id);
                        setExpStatusFilter('all');
                        setSelectedForTransfer(new Set());   // อย่าให้ติ๊กค้างจากรายการที่ถูกซ่อน
                        setSelectedForVoucher(new Set());
                      }}
                      className={`w-full text-left flex items-center gap-3 px-4 py-2.5 transition-colors ${active ? 'bg-[#eef7c4]' : 'hover:bg-gray-100'}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${active ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-600'}`}>
                        {p.name?.[0]?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-medium text-gray-900">{p.name} <span className="text-gray-400 font-normal">· {p.count} รายการ</span></div>
                        <div className="flex gap-2 text-[9px] mt-0.5 flex-wrap">
                          {p.pending > 0   && <span className="text-yellow-600">รออนุมัติ ฿{p.pending.toLocaleString()}</span>}
                          {p.approved > 0  && <span className="text-blue-600">รอโอน ฿{p.approved.toLocaleString()}</span>}
                          {p.transferred > 0 && <span className="text-green-600">โอนแล้ว ฿{p.transferred.toLocaleString()}</span>}
                        </div>
                      </div>
                      <div className="text-[12px] font-semibold text-gray-900 flex-shrink-0">฿{p.total.toLocaleString()}</div>
                      <ChevronRight size={13} className={`flex-shrink-0 ${active ? 'text-gray-900 rotate-90' : 'text-gray-300'} transition-transform`}/>
                    </button>
                    );
                  })}
                </div>
                <div className="px-4 py-2 bg-gray-50 flex justify-between">
                  <span className="text-[10px] text-gray-500">รวมทั้งหมด</span>
                  <span className="text-[11px] font-bold text-gray-900">฿{mTotalAll.toLocaleString()}</span>
                </div>
              </div>
            )}

            {/* กำลังดูรายคน — แยกตามหมวดหมู่ + ปุ่มกลับไปดูทุกคน */}
            {selPerson && (
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-semibold text-gray-900 truncate">{selPerson.name}</div>
                    <div className="text-[9px] text-gray-400 truncate">{fmtMonthEn(expMonth)} · {selPerson.count} รายการ · ฿{selPerson.total.toLocaleString()}</div>
                  </div>
                  <button onClick={() => setExpPersonFilter(null)}
                    className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#f0f0f0] hover:bg-gray-200 text-[10px] text-gray-600 whitespace-nowrap">
                    <XCircle size={11}/> ดูทุกคน
                  </button>
                </div>
                <div className="px-4 py-3 space-y-2.5">
                  <div className="text-[10px] font-semibold text-gray-500">แยกตามหมวดหมู่</div>
                  {catRows.map(c => (
                    <div key={c.cat} className="space-y-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-[10px] text-gray-700 flex-1 min-w-0 truncate">{c.cat}</span>
                        <span className="text-[9px] text-gray-400 flex-shrink-0">{c.count} รายการ</span>
                        <span className="text-[11px] font-semibold text-gray-900 flex-shrink-0">฿{c.total.toLocaleString()}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gray-800 rounded-full"
                          style={{ width: catMax ? `${Math.max(3, (c.total / catMax) * 100)}%` : '0%' }}/>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* status filter tabs */}
            <div className="flex gap-1.5 flex-wrap">
              {[
                { key: 'all',         label: 'ทั้งหมด',       count: monthAll.length },
                { key: 'pending',     label: 'รออนุมัติ',     count: mPending.length },
                { key: 'approved',    label: 'อนุมัติ·รอโอน', count: mApproved.length },
                { key: 'transferred', label: 'โอนแล้ว',       count: mXfer.length },
              ].map(f => (
                <button key={f.key} onClick={() => setExpStatusFilter(f.key)}
                  className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${expStatusFilter === f.key ? 'bg-gray-900 text-white' : 'bg-[#f0f0f0] text-gray-600 hover:bg-gray-200'}`}>
                  {f.label} {f.count > 0 && <span className="ml-1 opacity-70">{f.count}</span>}
                </button>
              ))}
              {/* บัญชีกลาง 'admin' ไม่มีปุ่มเบิก — ให้ล็อกอินเป็น yok/Font แล้วเบิกในชื่อตัวเอง */}
              {user?.id !== 'admin' && (
              <button onClick={() => setShowExpForm(!showExpForm)}
                className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-[#d4f542] rounded-lg text-[11px] font-semibold text-gray-900">
                <Plus size={12}/> เพิ่ม
              </button>
              )}
            </div>

            {user?.id !== 'admin' && showExpForm && <ExpenseForm
              form={expForm} setForm={setExpForm}
              receiptFile={receiptFile} setReceiptFile={setReceiptFile}
              fileRef={fileRef}
              onSubmit={handleExpSubmit}
              onCancel={() => { setShowExpForm(false); setReceiptFile(null); }}
              submitting={expSubmitting}
              cats={EXPENSE_CATS}
              descOptions={expSuggest.descriptions}
              projectOptions={expSuggest.projects}
            />}

            {/* select-all for approved items */}
            {expStatusFilter === 'approved' && filtered.length > 0 && (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    if (selectedForTransfer.size === filtered.length) setSelectedForTransfer(new Set());
                    else setSelectedForTransfer(new Set(filtered.map(e => e.id)));
                  }}
                  className="flex items-center gap-1.5 text-[11px] text-gray-600 hover:text-gray-900"
                >
                  {selectedForTransfer.size === filtered.length && filtered.length > 0
                    ? <CheckSquare size={16} className="text-gray-900"/>
                    : <Square size={16} className="text-gray-300"/>}
                  เลือกทั้งหมด ({filtered.length} รายการ)
                </button>
                {selectedForTransfer.size > 0 && (
                  <button onClick={() => setSelectedForTransfer(new Set())} className="text-[10px] text-gray-400 hover:text-gray-700">
                    ยกเลิกการเลือก
                  </button>
                )}
              </div>
            )}

            {allExpLoading ? (
              <div className="text-center py-8 text-[12px] text-gray-400">กำลังโหลด...</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-10 text-[12px] text-gray-400">
                <Receipt size={28} className="mx-auto mb-2 text-gray-300"/>
                ไม่มีรายการ
              </div>
            ) : (
              <>
                <ExpenseList items={filtered} showActions={true}
                  onApprove={handleExpApprove} onReject={handleExpReject}
                  onTransfer={handleTransfer} onEdit={handleExpEdit} onDelete={handleExpDelete} onRevert={handleExpRevert} showUser
                  onUploadReceipt={handleReceiptUpload} onClearReceipt={handleReceiptClear} uploadingReceiptId={uploadingReceiptId}
                  showSelect={expStatusFilter === 'transferred'}
                  selectedIds={selectedForVoucher}
                  onToggleSelect={(id) => setSelectedForVoucher(prev => {
                    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
                  })}
                  showTransferSelect={expStatusFilter === 'approved'}
                  selectedTransferIds={selectedForTransfer}
                  onToggleTransfer={(id) => setSelectedForTransfer(prev => {
                    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
                  })}
                  onReceiptStatusChange={(id, status) => setAllExpenses(prev =>
                    prev.map(e => e.id === id ? { ...e, receiptStatus: status } : e)
                  )}
                />

                {/* ── bulk transfer action bar ── */}
                {selectedForTransfer.size > 0 && (() => {
                  const selItems = allExpenses.filter(e => selectedForTransfer.has(e.id));
                  const selTotal = selItems.reduce((s,e) => s + Number(e.amount||0), 0);
                  return (
                    <div className="sticky bottom-4 bg-[#111] text-white rounded-xl px-4 py-3 space-y-2.5 shadow-xl">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-[12px] font-semibold">เลือก {selectedForTransfer.size} รายการ · ฿{selTotal.toLocaleString()}</div>
                          <div className="text-[10px] text-gray-400">บันทึกการโอนเงินคืนพร้อมกัน</div>
                        </div>
                        <button onClick={() => setSelectedForTransfer(new Set())} className="text-gray-500 hover:text-white text-[11px]">ยกเลิก</button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] text-gray-400 mb-1">วันที่โอน *</label>
                          <input type="date" value={bulkTransferDate} max={today}
                            onChange={e => setBulkTransferDate(e.target.value)}
                            className="w-full bg-white/10 border border-white/20 rounded-lg px-2.5 py-1.5 text-[11px] text-white outline-none focus:border-white/50"/>
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-400 mb-1">แนบสลิปรวม (ถ้ามี)</label>
                          <div onClick={() => bulkSlipRef.current?.click()}
                            className="border border-dashed border-white/20 rounded-lg px-2.5 py-1.5 text-[11px] cursor-pointer hover:border-white/40 flex items-center gap-1.5">
                            {bulkTransferFile
                              ? <><FileCheck size={12} className="text-[#d4f542] flex-shrink-0"/><span className="text-white truncate max-w-[80px]">{bulkTransferFile.name}</span></>
                              : <><CloudUpload size={12} className="text-gray-400"/><span className="text-gray-400">อัปโหลด</span></>}
                          </div>
                          <input ref={bulkSlipRef} type="file" accept="image/*,.pdf" className="hidden"
                            onChange={e => setBulkTransferFile(e.target.files[0] || null)}/>
                        </div>
                      </div>
                      <button onClick={handleBulkTransfer} disabled={bulkTransferring || !bulkTransferDate}
                        className="w-full py-2.5 bg-[#d4f542] text-gray-900 rounded-xl text-[12px] font-semibold disabled:opacity-50">
                        {bulkTransferring ? 'กำลังบันทึก...' : `บันทึกการโอน ${selectedForTransfer.size} รายการ`}
                      </button>
                    </div>
                  );
                })()}

                {/* ── voucher action bar ── */}
                {selectedForVoucher.size > 0 && (() => {
                  const selItems = allExpenses.filter(e => selectedForVoucher.has(e.id));
                  const selTotal = selItems.reduce((s,e) => s + Number(e.amount||0), 0);
                  const staffIds = [...new Set(selItems.map(e => e.userId))];
                  const staffName = selItems[0]?.userName || '';
                  return (
                    <div className="sticky bottom-4 bg-[#d4f542] rounded-xl px-4 py-3 flex items-center justify-between gap-3 shadow-lg">
                      <div>
                        <div className="text-[12px] font-semibold text-gray-900">เลือก {selectedForVoucher.size} รายการ · ฿{selTotal.toLocaleString()}</div>
                        <div className="text-[10px] text-gray-700">{staffIds.length === 1 ? staffName : <span className="text-red-600">เลือกหลาย staff — ต้องเลือกคนเดียวกัน</span>}</div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => setSelectedForVoucher(new Set())}
                          className="px-3 py-2 bg-white/60 rounded-lg text-[11px] text-gray-700">ยกเลิก</button>
                        <button onClick={handleCreateVoucher} disabled={staffIds.length > 1}
                          className="px-4 py-2 bg-gray-900 text-white rounded-lg text-[11px] font-semibold disabled:opacity-40 flex items-center gap-1.5">
                          <FileText size={12}/> สร้างใบสำคัญจ่าย
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        );
      })()}

      {/* ════════════════════════════════════════════════════════════════════
          TAB: VOUCHERS ADMIN (admin — all payment vouchers history)
      ════════════════════════════════════════════════════════════════════ */}
      {tab === 'vouchers_admin' && isAdmin() && (() => {
        const pendingCount = allVouchers.filter(v => v.status === 'pending_signature').length;
        const signedCount  = allVouchers.filter(v => v.status === 'signed').length;

        // group by month for display
        const byMonth = {};
        allVouchers.forEach(v => {
          const m = v.month || v.voucherNo?.slice(3, 9)?.replace(/(\d{4})(\d{2})/, '$1-$2') || '—';
          if (!byMonth[m]) byMonth[m] = [];
          byMonth[m].push(v);
        });
        const months = Object.keys(byMonth).sort((a, b) => b.localeCompare(a));

        return (
          <div className="space-y-4">
            {/* summary stats */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-[#f8f8f8] rounded-xl p-3">
                <div className="text-[9px] text-gray-400">ทั้งหมด</div>
                <div className="text-[15px] font-bold text-gray-900">{allVouchers.length} ใบ</div>
                <div className="text-[9px] text-gray-400">฿{allVouchers.reduce((s,v) => s + Number(v.totalAmount||0), 0).toLocaleString()}</div>
              </div>
              <div className="bg-[#fef3c7] rounded-xl p-3">
                <div className="text-[9px] text-gray-400">รอเซ็นรับเงิน</div>
                <div className="text-[15px] font-bold text-amber-700">{pendingCount} ใบ</div>
                <div className="text-[9px] text-gray-400">฿{allVouchers.filter(v=>v.status==='pending_signature').reduce((s,v)=>s+Number(v.totalAmount||0),0).toLocaleString()}</div>
              </div>
            </div>

            {allVouchers.length === 0 ? (
              <div className="text-center py-12 text-[12px] text-gray-400">
                <FileText size={28} className="mx-auto mb-2 text-gray-300"/>
                ยังไม่มีใบสำคัญจ่าย
              </div>
            ) : (
              months.map(m => (
                <div key={m} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold text-gray-500">{fmtMonthEn(m)}</span>
                    <div className="flex-1 h-px bg-gray-100"/>
                    <span className="text-[10px] text-gray-400">{byMonth[m].length} ใบ</span>
                  </div>
                  {byMonth[m].map(v => {
                    const isPending = v.status === 'pending_signature';
                    return (
                      <div key={v.id} className="bg-white border border-gray-100 rounded-xl p-4 space-y-3 shadow-sm">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="text-[12px] font-semibold text-gray-900">{v.voucherNo}</div>
                            <div className="text-[11px] text-gray-600 mt-0.5">
                              {v.staffFullName || v.staffName}
                            </div>
                            <div className="text-[10px] text-gray-400 mt-0.5">
                              {(v.items||[]).length} รายการ · ฿{Number(v.totalAmount||0).toLocaleString()}
                            </div>
                            {v.transferDate && (
                              <div className="text-[10px] text-blue-600 mt-0.5">
                                โอนเงิน {new Date(v.transferDate).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })}
                              </div>
                            )}
                          </div>
                          <span style={isPending
                            ? { background:'#fef3c7', color:'#b45309' }
                            : { background:'#f0fdf4', color:'#16a34a' }}
                            className="text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                            {isPending ? 'รอเซ็น' : 'เซ็นแล้ว'}
                          </span>
                        </div>
                        {v.staffBankName && (
                          <div className="text-[10px] text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
                            {v.staffBankName} · {v.staffBankAccount} · {v.staffBankAccountName || v.staffFullName || v.staffName}
                          </div>
                        )}
                        <div className="grid grid-cols-3 gap-1.5">
                          <button onClick={() => openVoucherPrint(v)}
                            className="col-span-3 flex items-center justify-center gap-1.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-[11px] text-gray-700 hover:bg-gray-100 transition-colors">
                            <Printer size={12}/> ดูเอกสาร / พิมพ์
                          </button>
                          <button onClick={() => handleToggleVoucherStatus(v.id, v.status)}
                            className="col-span-2 flex items-center justify-center gap-1 py-1.5 bg-white border border-gray-200 rounded-lg text-[11px] text-gray-600 hover:bg-gray-50 transition-colors">
                            <RefreshCw size={11}/>
                            {isPending ? 'ทำเครื่องหมายเซ็นแล้ว' : 'คืนเป็นรอเซ็น'}
                          </button>
                          <button onClick={() => handleDeleteVoucher(v.id, v.voucherNo)}
                            className="flex items-center justify-center gap-1 py-1.5 bg-white border border-red-100 rounded-lg text-[11px] text-red-500 hover:bg-red-50 transition-colors">
                            <Trash2 size={11}/> ลบ
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        );
      })()}

      {/* ════════════════════════════════════════════════════════════════════
          TAB: PROFILE (change password)
      ════════════════════════════════════════════════════════════════════ */}
      {/* ════════════════════════════════════════════════════════════════════
          TAB: ACCOUNTS ADMIN — view all staff profile & bank info
      ════════════════════════════════════════════════════════════════════ */}
      {tab === 'accounts_admin' && isAdmin() && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-[12px] font-semibold text-gray-700">
            <Users size={14}/> ข้อมูลบัญชีพนักงานทั้งหมด
          </div>
          {allProfilesLoading ? (
            <div className="text-center py-10 text-[12px] text-gray-400">กำลังโหลด...</div>
          ) : (
            <div className="space-y-2">
              {ALL_USERS.map(u => {
                const p = allProfiles.find(x => x.userId === u.id) || {};
                const hasBank = p.bankAccount || p.bankName;
                return (
                  <div key={u.id} className="bg-[#f8f8f8] rounded-xl overflow-hidden">
                    {/* header */}
                    <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
                      <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-[13px] font-bold text-gray-600 flex-shrink-0">
                        {u.name[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[13px] font-semibold text-gray-900">
                            {(p.firstName && p.lastName) ? `${p.firstName} ${p.lastName}` : u.name}
                          </span>
                          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded ${u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-200 text-gray-600'}`}>
                            {u.role === 'admin' ? 'Admin' : 'Staff'}
                          </span>
                        </div>
                        <div className="text-[10px] text-gray-400">@{u.id}</div>
                      </div>
                      {!hasBank && (
                        <span className="text-[9px] text-gray-400 bg-gray-200 px-2 py-0.5 rounded-full flex-shrink-0">ยังไม่มีข้อมูล</span>
                      )}
                    </div>
                    {/* detail rows */}
                    <div className="px-4 py-3 space-y-2">
                      <div className="flex items-center gap-2 text-[11px]">
                        <Phone size={11} className="text-gray-400 flex-shrink-0"/>
                        <span className="text-gray-500">เบอร์โทร:</span>
                        <span className="text-gray-900 font-medium">{p.phone || <span className="text-gray-300">—</span>}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[11px]">
                        <Building size={11} className="text-gray-400 flex-shrink-0"/>
                        <span className="text-gray-500">ธนาคาร:</span>
                        <span className="text-gray-900 font-medium">{p.bankName || <span className="text-gray-300">—</span>}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[11px]">
                        <CreditCard size={11} className="text-gray-400 flex-shrink-0"/>
                        <span className="text-gray-500">เลขบัญชี:</span>
                        <span className="text-gray-900 font-medium font-mono tracking-wide">{p.bankAccount || <span className="text-gray-300">—</span>}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[11px]">
                        <Users size={11} className="text-gray-400 flex-shrink-0"/>
                        <span className="text-gray-500">ชื่อบัญชี:</span>
                        <span className="text-gray-900 font-medium">{p.bankAccountName || <span className="text-gray-300">—</span>}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === 'profile' && (
        <div className="max-w-sm space-y-4">
          <SectionCard>
            <div className="flex items-center gap-2 text-[12px] font-semibold text-gray-900 mb-4">
              <Lock size={14}/> เปลี่ยนรหัสผ่าน
            </div>
            <form onSubmit={handlePwChange} className="space-y-3">
              {[
                { label: 'รหัสผ่านปัจจุบัน', key: 'current' },
                { label: 'รหัสผ่านใหม่',     key: 'next' },
                { label: 'ยืนยันรหัสผ่านใหม่', key: 'confirm' },
              ].map(({ label, key }) => (
                <div key={key}>
                  <label className="block text-[10px] text-gray-400 mb-1">{label}</label>
                  <input
                    type="password"
                    value={pwForm[key]}
                    onChange={e => setPwForm({ ...pwForm, [key]: e.target.value })}
                    className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-gray-400"
                    autoComplete="new-password"
                  />
                </div>
              ))}
              <button type="submit" disabled={pwLoading}
                className="w-full py-2.5 bg-gray-900 text-white rounded-lg text-[12px] font-medium mt-1 disabled:opacity-50">
                {pwLoading ? 'กำลังบันทึก...' : 'บันทึกรหัสผ่านใหม่'}
              </button>
            </form>
          </SectionCard>

          <SectionCard>
            <div className="text-[12px] font-semibold text-gray-900 mb-3">ข้อมูลส่วนตัว &amp; บัญชีธนาคาร</div>
            <form onSubmit={handleProfileSave} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] text-gray-400 mb-1">ชื่อจริง</label>
                  <input value={profile.firstName} onChange={e => setProfile({ ...profile, firstName: e.target.value })}
                    placeholder="ชื่อ" className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-gray-400"/>
                </div>
                <div>
                  <label className="block text-[10px] text-gray-400 mb-1">นามสกุล</label>
                  <input value={profile.lastName} onChange={e => setProfile({ ...profile, lastName: e.target.value })}
                    placeholder="นามสกุล" className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-gray-400"/>
                </div>
              </div>
              <div>
                <label className="block text-[10px] text-gray-400 mb-1">เบอร์โทร</label>
                <input value={profile.phone} onChange={e => setProfile({ ...profile, phone: e.target.value })}
                  placeholder="08x-xxx-xxxx" type="tel"
                  className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-gray-400"/>
              </div>
              <div className="pt-1 border-t border-gray-100">
                <div className="text-[10px] text-gray-400 mb-2 font-medium">ข้อมูลบัญชีธนาคาร (สำหรับใบสำคัญจ่าย)</div>
                <div className="space-y-2">
                  <div>
                    <label className="block text-[10px] text-gray-400 mb-1">ธนาคาร</label>
                    <input value={profile.bankName} onChange={e => setProfile({ ...profile, bankName: e.target.value })}
                      placeholder="เช่น กสิกรไทย, กรุงไทย" className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-gray-400"/>
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-400 mb-1">เลขบัญชี</label>
                    <input value={profile.bankAccount} onChange={e => setProfile({ ...profile, bankAccount: e.target.value })}
                      placeholder="xxx-x-xxxxx-x" className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-gray-400"/>
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-400 mb-1">ชื่อบัญชี</label>
                    <input value={profile.bankAccountName} onChange={e => setProfile({ ...profile, bankAccountName: e.target.value })}
                      placeholder="ชื่อ-นามสกุล บนบัญชี" className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none focus:border-gray-400"/>
                  </div>
                </div>
              </div>
              <button type="submit" disabled={profileSaving}
                className="w-full py-2.5 bg-gray-900 text-white rounded-lg text-[12px] font-medium disabled:opacity-50">
                {profileSaving ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
              </button>
            </form>
          </SectionCard>

          <SectionCard>
            <div className="text-[11px] text-gray-400 space-y-1">
              <div className="text-[12px] font-medium text-gray-700 mb-2">ข้อมูลบัญชี</div>
              <div>Username: <span className="text-gray-900 font-medium">@{user.id}</span></div>
              <div>ชื่อ: <span className="text-gray-900 font-medium">{user.name}</span></div>
              <div>Role: <span className="text-gray-900 font-medium">{user.role === 'admin' ? 'Admin' : 'Staff'}</span></div>
            </div>
          </SectionCard>
        </div>
      )}
      </div>{/* /content */}
    </div>
  );
}

// ── Voucher Preview Modal ────────────────────────────────────────────────────
function VoucherPreviewModal({ data, creating, onClose, onConfirm }) {
  const { staffName, staffProfile, adminProfile, adminName, voucherNo } = data;
  const [items, setItems] = useState(data.items);
  const [transferDate, setTransferDate] = useState(data.transferDate || '');
  const total = items.reduce((s, i) => s + Number(i.amount || 0), 0);
  const staffFullName = (staffProfile.firstName && staffProfile.lastName)
    ? `${staffProfile.firstName} ${staffProfile.lastName}` : staffName;
  const adminFullName = (adminProfile.firstName && adminProfile.lastName)
    ? `${adminProfile.firstName} ${adminProfile.lastName}` : adminName;
  const issueDate = new Date().toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' });
  const dot = (color, size = 6) => (
    <span style={{ width: size, height: size, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }}/>
  );
  const receiptLabel = (s) => {
    if (s === 'tax_invoice') return <span className="inline-flex items-center gap-1 text-[10px] text-amber-800">{dot('#f59e0b', 5)}ใบกำกับภาษี</span>;
    if (s === 'receipt')     return <span className="inline-flex items-center gap-1 text-[10px] text-green-700">{dot('#16a34a', 5)}มีใบเสร็จ</span>;
    return <span className="inline-flex items-center gap-1 text-[10px] text-gray-400">{dot('#9ca3af', 5)}ไม่มีใบเสร็จ</span>;
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden my-4 shadow-2xl">
        {/* modal header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-[#f8f8f8]">
          <div className="flex items-center gap-2">
            <Eye size={15} className="text-gray-500"/>
            <span className="text-[13px] font-semibold text-gray-900">ตรวจสอบก่อนส่ง</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-500">ส่งถึง: {staffFullName}</span>
            <span className="text-[9px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800">รอลายเซ็น</span>
          </div>
        </div>

        {/* company header */}
        <div className="flex justify-between items-start px-4 py-3 border-b border-gray-100">
          <div className="flex items-start gap-2">
            <img src="/LOGO_FYM-01.svg" alt="FYM" style={{ width: 72, height: 'auto' }}/>
            <div className="pt-0.5">
              <div className="text-[11px] font-bold text-gray-900">บริษัท แฟลชยัวร์มีม จำกัด</div>
              <div className="text-[9px] text-gray-500">เลขทะเบียนนิติบุคคล 0145565003261</div>
              <div className="text-[9px] text-gray-400 leading-relaxed">628/164 ถนนเลียบคลองรังสิต<br/>ต.ประชาธิปัตย์ อ.ธัญบุรี จ.ปทุมธานี 12130</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[18px] font-bold text-gray-900">ใบสำคัญจ่าย</div>
            <div className="text-[9px] text-gray-400">Payment Voucher</div>
            <div className="mt-1 inline-flex items-center gap-1.5 bg-gray-100 border border-gray-200 rounded px-2 py-0.5 text-[10px]">
              <span className="text-gray-500">เลขที่</span><span className="font-bold text-gray-900">{voucherNo}</span>
            </div>
          </div>
        </div>

        {/* meta */}
        <div className="grid grid-cols-2 border-b border-gray-100 bg-[#fafafa]">
          <div className="px-4 py-2.5 border-r border-gray-100">
            <div className="text-[8px] text-gray-400 font-bold uppercase tracking-wide mb-1">จ่ายให้</div>
            <div className="text-[12px] font-bold text-gray-900">{staffFullName}</div>
            {staffProfile.phone && <div className="text-[9px] text-gray-400 mt-0.5">{staffProfile.phone}</div>}
          </div>
          <div className="px-4 py-2.5 grid grid-cols-2 gap-x-3 gap-y-2">
            <div><div className="text-[8px] text-gray-400 font-bold uppercase tracking-wide mb-0.5">วันที่ออก</div><div className="text-[10px] font-semibold text-gray-900">{issueDate}</div></div>
            <div><div className="text-[8px] text-gray-400 font-bold uppercase tracking-wide mb-0.5">อนุมัติโดย</div><div className="text-[10px] font-semibold text-gray-900">{adminFullName}</div></div>
            <div className="col-span-2">
              <div className="text-[8px] text-gray-400 font-bold uppercase tracking-wide mb-0.5">วันที่บริษัทโอนเงินคืน</div>
              <input type="date" value={transferDate} max={new Date().toISOString().slice(0,10)}
                onChange={e => setTransferDate(e.target.value)}
                className="w-full bg-white border border-gray-200 rounded px-2 py-1 text-[10px] text-gray-900 outline-none focus:border-gray-400"/>
            </div>
          </div>
        </div>


        {/* items table */}
        <div className="px-4 pt-2 pb-1">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="pb-1.5 text-left text-[8px] font-bold text-gray-400 uppercase tracking-wide w-5">#</th>
                <th className="pb-1.5 text-left text-[8px] font-bold text-gray-400 uppercase tracking-wide">รายละเอียด</th>
                <th className="pb-1.5 text-left text-[8px] font-bold text-gray-400 uppercase tracking-wide w-16">หลักฐาน</th>
                <th className="pb-1.5 text-right text-[8px] font-bold text-gray-400 uppercase tracking-wide w-16">จำนวนเงิน</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={it.id} className="border-b border-gray-50">
                  <td className="py-1.5 text-gray-400">{i + 1}</td>
                  <td className="py-1.5">
                    <span className="text-gray-900 font-medium">{it.description}</span>
                    <span className="ml-1.5 text-[9px] text-gray-400">{it.category}</span>
                  </td>
                  <td className="py-1.5">{receiptLabel(it.receiptStatus)}</td>
                  <td className="py-1.5 text-right font-semibold text-gray-900">฿{Number(it.amount).toLocaleString()}</td>
                </tr>
              ))}
              <tr>
                <td colSpan={3} className="pt-2 text-right text-[10px] text-gray-400">รวมทั้งสิ้น</td>
                <td className="pt-2 text-right font-bold text-[13px] text-gray-900">฿{total.toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* bank info if available */}
        {(staffProfile.bankName || staffProfile.bankAccount) && (
          <div className="mx-4 mb-2 bg-[#f9fafb] border border-gray-200 rounded-lg px-3 py-2.5">
            <div className="text-[8px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">ข้อมูลบัญชีรับโอน</div>
            <div className="flex justify-between text-[10px]"><span className="text-gray-400">ธนาคาร</span><span className="font-semibold">{staffProfile.bankName}</span></div>
            <div className="flex justify-between text-[10px]"><span className="text-gray-400">เลขบัญชี</span><span className="font-semibold">{staffProfile.bankAccount}</span></div>
            {staffProfile.bankAccountName && <div className="flex justify-between text-[10px]"><span className="text-gray-400">ชื่อบัญชี</span><span className="font-semibold">{staffProfile.bankAccountName}</span></div>}
          </div>
        )}

        {/* sigs */}
        <div className="grid grid-cols-2 gap-8 px-4 pt-2 pb-3 border-t border-gray-100">
          {[{ name: staffFullName, role: 'ผู้รับเงิน', en: 'Payee', pending: true }, { name: adminFullName, role: 'ผู้อนุมัติจ่าย', en: 'Authorised by' }].map(s => (
            <div key={s.role} className="text-center">
              <div className="border-b border-dashed border-gray-300 h-7 mb-1.5 relative">
                {s.pending && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 text-[8px] text-gray-300 whitespace-nowrap">— รอลายเซ็น —</span>}
              </div>
              <div className="text-[11px] font-bold text-gray-900">{s.name}</div>
              <div className="text-[10px] text-gray-500">{s.role}</div>
            </div>
          ))}
        </div>

        {/* actions */}
        <div className="flex gap-2 px-4 py-3 border-t border-gray-100 bg-[#f8f8f8]">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-[12px] font-medium text-gray-700 bg-white hover:bg-gray-50 flex items-center justify-center gap-1.5">
            <ChevronLeft size={14}/> แก้ไข
          </button>
          <button onClick={() => onConfirm(items, transferDate)} disabled={creating || !items.length}
            className="flex-[2] py-2.5 bg-[#111] text-white rounded-xl text-[12px] font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
            {creating ? 'กำลังสร้าง...' : <><FileText size={13}/> ส่งให้พนักงาน</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Expense shared components ────────────────────────────────────────────────
function ExpenseForm({ form, setForm, receiptFile, setReceiptFile, fileRef, onSubmit, onCancel, submitting, cats, descOptions = [], projectOptions = [] }) {
  return (
    <div className="bg-[#f8f8f8] rounded-xl p-4 space-y-3">
      {/* Suggestions from the user's own previous claims */}
      <datalist id="exp-desc-suggest">{descOptions.map(v => <option key={v} value={v} />)}</datalist>
      <datalist id="exp-project-suggest">{projectOptions.map(v => <option key={v} value={v} />)}</datalist>
      <div className="text-[12px] font-semibold text-gray-900">รายการเบิกเงินใหม่</div>
      <form onSubmit={onSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] text-gray-400 mb-1">หมวดหมู่</label>
            <select value={form.category} onChange={e=>setForm({...form,category:e.target.value})}
              className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none">
              {cats.map(c=><option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] text-gray-400 mb-1">จำนวนเงิน (฿)</label>
            <input type="number" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})}
              placeholder="0" className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none"/>
          </div>
        </div>
        <div>
          <label className="block text-[10px] text-gray-400 mb-1">รายละเอียด</label>
          <input type="text" list="exp-desc-suggest" autoComplete="off" value={form.description} onChange={e=>setForm({...form,description:e.target.value})}
            placeholder="อธิบายรายการ..." className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none"/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] text-gray-400 mb-1">วันที่ใช้จ่าย</label>
            <input type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})}
              className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none"/>
          </div>
          <div>
            <label className="block text-[10px] text-gray-400 mb-1">โปรเจกต์/งาน</label>
            <input type="text" list="exp-project-suggest" autoComplete="off" value={form.project} onChange={e=>setForm({...form,project:e.target.value})}
              placeholder="ระบุงาน (ถ้ามี)" className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-[12px] text-gray-900 outline-none"/>
          </div>
        </div>
        <div>
          <label className="block text-[10px] text-gray-400 mb-2">สถานะใบเสร็จ / หลักฐาน</label>
          <div className="flex gap-2 flex-wrap">
            {[
              { key: 'none',         label: 'ไม่มีใบเสร็จ', dotColor: '#9ca3af', activeBg: '#f3f4f6',  activeBorder: '#6b7280',  activeText: '#374151' },
              { key: 'receipt',      label: 'มีใบเสร็จ',     dotColor: '#16a34a', activeBg: '#dcfce7',  activeBorder: '#16a34a',  activeText: '#15803d' },
              { key: 'tax_invoice',  label: 'มีใบกำกับภาษี', dotColor: '#f59e0b', activeBg: '#fef3c7',  activeBorder: '#d97706',  activeText: '#92400e' },
            ].map(opt => {
              const active = form.receiptStatus === opt.key;
              return (
                <button key={opt.key} type="button"
                  onClick={() => setForm({ ...form, receiptStatus: opt.key })}
                  style={active ? { background: opt.activeBg, borderColor: opt.activeBorder, color: opt.activeText } : {}}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-medium transition-all ${active ? '' : 'border-gray-200 text-gray-400 bg-white hover:border-gray-300'}`}
                >
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: opt.dotColor, flexShrink: 0, display: 'inline-block' }}/>
                  {opt.label}
                </button>
              );
            })}
          </div>
          {form.receiptStatus === 'tax_invoice' && (
            <div className="mt-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
              รายการนี้จะต้องออกใบสำคัญรับเงินแยกจากรายการอื่น
            </div>
          )}
        </div>
        <div>
          <label className="block text-[10px] text-gray-400 mb-1">แนบใบเสร็จ / สลิป</label>
          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center cursor-pointer hover:border-gray-400 bg-white transition-colors"
          >
            {receiptFile ? (
              <div className="flex items-center justify-center gap-2 text-[11px] text-blue-500">
                <FileCheck size={16}/>{receiptFile.name}
              </div>
            ) : (
              <>
                <CloudUpload size={22} className="mx-auto mb-1.5 text-gray-300"/>
                <div className="text-[11px] text-gray-400">คลิกเพื่ออัปโหลด หรือลาก file มาวาง</div>
                <div className="text-[9px] text-gray-300 mt-0.5">JPG, PNG, PDF — ไม่เกิน 10MB</div>
              </>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden"
            onChange={e => setReceiptFile(e.target.files[0] || null)}/>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel}
            className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-[12px] text-gray-600">ยกเลิก</button>
          <button type="submit" disabled={submitting}
            className="flex-1 py-2 bg-gray-900 text-white rounded-lg text-[12px] font-medium disabled:opacity-50">
            {submitting ? 'กำลังส่ง...' : 'ส่งคำขอเบิก'}
          </button>
        </div>
      </form>
    </div>
  );
}

function ExpenseList({ items, showActions, onApprove, onReject, onTransfer, onEdit, onDelete, onRevert, showUser, showSelect, selectedIds, onToggleSelect, showTransferSelect, selectedTransferIds, onToggleTransfer, onReceiptStatusChange, onUploadReceipt, onClearReceipt, uploadingReceiptId }) {
  const todayStr = new Date().toISOString().slice(0,10);
  const [expanded, setExpanded]       = useState(null);
  const [xferItem, setXferItem]       = useState(null); // { id, userId, date, file }
  const [xferring, setXferring]       = useState(false);
  const xferFileRef                   = useRef(null);
  const [updatingStatus, setUpdatingStatus] = useState(null); // claimId being updated
  const [editingId, setEditingId]     = useState(null);
  const [editForm, setEditForm]       = useState({});
  const [editSaving, setEditSaving]   = useState(false);

  async function handleReceiptStatusChange(claimId, newStatus) {
    setUpdatingStatus(claimId);
    try {
      await updateExpenseReceiptStatus(claimId, newStatus);
      if (onReceiptStatusChange) onReceiptStatusChange(claimId, newStatus);
    } catch { }
    setUpdatingStatus(null);
  }

  async function submitTransfer(e) {
    e.preventDefault();
    if (!xferItem.date) { return; }
    setXferring(true);
    await onTransfer(xferItem.id, xferItem.userId, xferItem.file, xferItem.date);
    setXferItem(null);
    setXferring(false);
  }

  if (items.length === 0) return (
    <div className="text-center py-10 text-[12px] text-gray-400">
      <Receipt size={28} className="mx-auto mb-2 text-gray-300"/>ยังไม่มีรายการ
    </div>
  );
  return (
    <div className="space-y-2">
      {items.map((item) => {
        const isOpen   = expanded === item.id;
        const isXfer   = xferItem?.id === item.id;
        return (
          <div key={item.id} className="bg-[#f8f8f8] rounded-xl overflow-hidden">
            {/* ── header row ── */}
            <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setExpanded(isOpen ? null : item.id)}>
              {showTransferSelect && item.status === 'approved' && (
                <div onClick={e => { e.stopPropagation(); onToggleTransfer(item.id); }} className="flex-shrink-0">
                  {selectedTransferIds?.has(item.id)
                    ? <CheckSquare size={18} className="text-gray-900"/>
                    : <Square size={18} className="text-gray-300"/>}
                </div>
              )}
              {showSelect && item.status === 'transferred' && (
                item.voucherId
                  ? <div className="flex-shrink-0 flex items-center gap-1" title={`ออกใบแล้ว ${item.voucherNo || ''}`}>
                      <Lock size={13} className="text-gray-300"/>
                    </div>
                  : <div onClick={e => { e.stopPropagation(); onToggleSelect(item.id); }} className="flex-shrink-0">
                      {selectedIds?.has(item.id)
                        ? <CheckSquare size={18} className="text-gray-900"/>
                        : <Square size={18} className="text-gray-300"/>}
                    </div>
              )}
              <div className="w-8 h-8 rounded-lg bg-white border border-gray-100 flex items-center justify-center flex-shrink-0">
                {item.status === 'transferred'
                  ? <Banknote size={14} className="text-green-500"/>
                  : <Receipt size={14} className="text-gray-400"/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {showUser && <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">{item.userName}</span>}
                  <span className="text-[12px] font-medium text-gray-900 truncate">{item.description}</span>
                  {item.receiptStatus === 'tax_invoice' && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-[9px] font-semibold text-amber-800 flex-shrink-0">
                      <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#f59e0b', display: 'inline-block' }}/>
                      ใบกำกับภาษี
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-[10px] text-gray-400 mt-0.5 flex-wrap">
                  <span className="bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">{item.category}</span>
                  <span>{formatDate(item.date)}</span>
                  {item.project && <span className="text-purple-500">#{item.project}</span>}
                  {item.voucherStatus === 'signed' && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-50 border border-green-200 text-[9px] font-semibold text-green-700 flex-shrink-0">
                      <Check size={9}/> เซ็นใบสำคัญแล้ว · {item.voucherNo}
                    </span>
                  )}
                  {item.voucherStatus === 'pending_signature' && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[9px] font-semibold text-amber-700 flex-shrink-0">
                      <FileText size={9}/> ออกใบแล้ว รอเซ็น · {item.voucherNo}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right flex-shrink-0 mr-1">
                <div className="text-[13px] font-semibold text-gray-900">฿{Number(item.amount).toLocaleString()}</div>
                <Badge status={item.status}/>
              </div>
              <ChevronDown size={14} className={`text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}/>
            </div>

            {/* ── expanded detail ── */}
            {isOpen && (
              <div className="border-t border-gray-200 px-4 pb-3 pt-2 space-y-2">

                {/* ── inline edit form (admin only, not signed) ── */}
                {showActions && onEdit && editingId === item.id ? (
                  <div className="bg-gray-50 rounded-xl p-3 space-y-2.5">
                    <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">แก้ไขรายการ</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="col-span-2">
                        <label className="block text-[10px] text-gray-400 mb-1">รายละเอียด</label>
                        <input value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                          className="w-full bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 text-[12px] text-gray-900 outline-none focus:border-gray-400"/>
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-400 mb-1">หมวดหมู่</label>
                        <select value={editForm.category} onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))}
                          className="w-full bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 text-[12px] text-gray-900 outline-none focus:border-gray-400">
                          {['ค่าเดินทาง','ค่าอาหาร','ค่าวัสดุ/อุปกรณ์','ค่าที่พัก','ค่าส่งพัสดุ','ค่าอินเตอร์เน็ต','ค่าซอฟต์แวร์','อื่นๆ'].map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-400 mb-1">ยอดเงิน (฿)</label>
                        <input type="number" min="0" step="0.01" value={editForm.amount} onChange={e => setEditForm(f => ({ ...f, amount: e.target.value }))}
                          className="w-full bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 text-[12px] text-gray-900 outline-none focus:border-gray-400"/>
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-400 mb-1">วันที่จ่าย</label>
                        <input type="date" value={editForm.date} max={todayStr} onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))}
                          className="w-full bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 text-[12px] text-gray-900 outline-none focus:border-gray-400"/>
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-400 mb-1">โปรเจกต์</label>
                        <input value={editForm.project} onChange={e => setEditForm(f => ({ ...f, project: e.target.value }))}
                          placeholder="ชื่อโปรเจกต์ (ถ้ามี)"
                          className="w-full bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 text-[12px] text-gray-900 outline-none focus:border-gray-400"/>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button onClick={() => setEditingId(null)}
                        className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-[11px] text-gray-500">ยกเลิก</button>
                      <button disabled={editSaving} onClick={async () => {
                        if (!editForm.amount || isNaN(Number(editForm.amount))) { toast.error('กรุณากรอกยอดเงิน'); return; }
                        setEditSaving(true);
                        try {
                          await onEdit(item.id, editForm);
                          toast.success('อัปเดตสำเร็จ');
                          setEditingId(null);
                        } catch { toast.error('อัปเดตไม่สำเร็จ'); }
                        setEditSaving(false);
                      }} className="flex-1 py-1.5 bg-gray-900 text-white rounded-lg text-[11px] font-semibold disabled:opacity-50">
                        {editSaving ? 'กำลังบันทึก...' : 'บันทึก'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    <div><span className="text-gray-400">รายละเอียด: </span><span className="text-gray-800">{item.description || '—'}</span></div>
                    <div><span className="text-gray-400">โปรเจกต์: </span><span className="text-gray-800">{item.project || '—'}</span></div>
                    <div><span className="text-gray-400">หมวดหมู่: </span><span className="text-gray-800">{item.category}</span></div>
                    <div><span className="text-gray-400">วันที่จ่าย: </span><span className="text-gray-800">{item.date}</span></div>
                    {showActions && onEdit && item.voucherStatus !== 'signed' && (
                      <div className="col-span-2 pt-0.5">
                        <button onClick={() => { setEditingId(item.id); setEditForm({ description: item.description || '', category: item.category || '', amount: item.amount || '', date: item.date || '', project: item.project || '' }); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-[11px] text-gray-600 hover:bg-gray-50">
                          <RefreshCw size={11}/> แก้ไขรายการ
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* admin: edit receipt status */}
                {showActions && (
                  <div className="pt-1">
                    <div className="text-[9px] text-gray-400 mb-1.5 font-medium uppercase tracking-wide">สถานะหลักฐาน</div>
                    <div className="flex gap-1.5 flex-wrap">
                      {[
                        { key: 'none',        label: 'ไม่มีใบเสร็จ',    dot: '#9ca3af', bg: '#f3f4f6', border: '#6b7280', text: '#374151' },
                        { key: 'receipt',     label: 'มีใบเสร็จ',        dot: '#16a34a', bg: '#dcfce7', border: '#16a34a', text: '#15803d' },
                        { key: 'tax_invoice', label: 'มีใบกำกับภาษี',   dot: '#f59e0b', bg: '#fef3c7', border: '#d97706', text: '#92400e' },
                      ].map(opt => {
                        const current = item.receiptStatus || 'none';
                        const active = current === opt.key;
                        const loading = updatingStatus === item.id;
                        return (
                          <button key={opt.key} disabled={loading}
                            onClick={() => !active && handleReceiptStatusChange(item.id, opt.key)}
                            style={active ? { background: opt.bg, borderColor: opt.border, color: opt.text } : {}}
                            className={`flex items-center gap-1 px-2.5 py-1 rounded-full border text-[10px] font-medium transition-all disabled:opacity-60 ${
                              active ? '' : 'border-gray-200 text-gray-400 bg-white hover:border-gray-300 cursor-pointer'
                            }`}
                          >
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: opt.dot, display: 'inline-block', flexShrink: 0 }}/>
                            {opt.label}
                            {active && <Check size={9} className="ml-0.5 opacity-70"/>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* staff receipt */}
                <div className="pt-0.5">
                  {item.receiptUrl ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <a href={item.receiptUrl} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-[11px] font-medium hover:bg-blue-100 transition-colors">
                        <Paperclip size={12}/> ดูใบเสร็จ / สลิปที่แนบ
                        {item.receiptFileName && <span className="text-blue-400 truncate max-w-[100px]">({item.receiptFileName})</span>}
                      </a>
                      {onUploadReceipt && (
                        <label className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${uploadingReceiptId === item.id ? 'bg-gray-100 text-gray-400' : 'bg-[#f0f0f0] text-gray-600 hover:bg-gray-200 cursor-pointer'}`}>
                          {uploadingReceiptId === item.id ? 'กำลังอัพโหลด…' : <><CloudUpload size={11}/> เปลี่ยน</>}
                          <input type="file" accept="image/*,application/pdf" className="hidden" disabled={uploadingReceiptId === item.id}
                            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onUploadReceipt(item.id, item.userId, f); }} />
                        </label>
                      )}
                      {onClearReceipt && (
                        <button type="button" disabled={uploadingReceiptId === item.id} onClick={() => onClearReceipt(item.id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-50">
                          <Trash2 size={11}/> ลบ
                        </button>
                      )}
                    </div>
                  ) : onUploadReceipt ? (
                    <div className="flex items-center gap-2">
                      <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${uploadingReceiptId === item.id ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-600 hover:bg-blue-100 cursor-pointer'}`}>
                        {uploadingReceiptId === item.id
                          ? <>กำลังอัพโหลด…</>
                          : <><CloudUpload size={12}/> แนบใบเสร็จ</>}
                        <input type="file" accept="image/*,application/pdf" className="hidden" disabled={uploadingReceiptId === item.id}
                          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onUploadReceipt(item.id, item.userId, f); }} />
                      </label>
                      <span className="inline-flex items-center gap-1 text-[10px] text-gray-400"><AlertCircle size={10}/> ยังไม่มีใบเสร็จ</span>
                    </div>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
                      <AlertCircle size={11}/> ไม่มีใบเสร็จแนบ
                    </span>
                  )}
                </div>

                {/* company transfer slip — shown to staff AND admin */}
                {item.status === 'transferred' && (
                  <div className="bg-green-50 rounded-lg p-2.5 space-y-1">
                    <div className="text-[10px] font-semibold text-green-700 flex items-center gap-1"><CheckCircle2 size={11}/>บริษัทโอนเงินคืนแล้ว</div>
                    {item.transferDate && (
                      <div className="text-[10px] text-green-600">วันที่โอน: {item.transferDate}</div>
                    )}
                    {item.transferSlipUrl ? (
                      <a href={item.transferSlipUrl} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-green-700 border border-green-200 rounded-lg text-[11px] font-medium hover:bg-green-50 transition-colors">
                        <Banknote size={12}/> ดูสลิปการโอน
                        {item.transferSlipFileName && <span className="text-green-500 truncate max-w-[100px]">({item.transferSlipFileName})</span>}
                        <ArrowUpRight size={10}/>
                      </a>
                    ) : (
                      <span className="text-[10px] text-green-600">— (ยังไม่มีสลิปแนบ)</span>
                    )}
                    {item.transferredBy && <div className="text-[9px] text-green-500">โอนโดย: {item.transferredBy}</div>}
                  </div>
                )}

                {/* admin actions */}
                {showActions && item.status === 'pending' && (
                  <div className="flex gap-2 pt-1">
                    <button onClick={() => onApprove(item.id, item.userId)}
                      className="flex-1 py-2 bg-[#d4f542] rounded-lg text-[11px] font-semibold text-gray-900">อนุมัติ</button>
                    <button onClick={() => onReject(item.id, item.userId)}
                      className="flex-1 py-2 bg-white border border-gray-200 rounded-lg text-[11px] text-gray-500 hover:border-red-200 hover:text-red-500">ปฏิเสธ</button>
                  </div>
                )}

                {/* delete — only for not-yet-approved claims (admin, or the owner's own list) */}
                {onDelete && (item.status === 'pending' || item.status === 'rejected') && (
                  <div className="pt-1">
                    <button onClick={() => onDelete(item.id, item.status)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-[11px] font-medium hover:bg-red-100 transition-colors">
                      <Trash2 size={12}/> ลบรายการ
                    </button>
                  </div>
                )}

                {/* admin: mark transferred + adjust status (approved, not yet transferred) */}
                {showActions && item.status === 'approved' && !isXfer && (
                  <div className="space-y-2 pt-1">
                    <button onClick={() => setXferItem({ id: item.id, userId: item.userId, date: todayStr, file: null })}
                      className="w-full py-2 bg-green-50 border border-green-200 text-green-700 rounded-lg text-[11px] font-medium hover:bg-green-100 transition-colors">
                      <Banknote size={12} className="inline mr-1.5"/>บันทึกการโอนเงินคืน
                    </button>
                    {(onRevert || onReject) && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-gray-400">ปรับสถานะ:</span>
                        {onRevert && (
                          <button onClick={() => onRevert(item.id, item.userId)}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#f0f0f0] text-gray-600 text-[11px] font-medium hover:bg-gray-200">
                            <RefreshCw size={11}/> กลับรออนุมัติ
                          </button>
                        )}
                        {onReject && (
                          <button onClick={() => { if (window.confirm('ปฏิเสธรายการที่อนุมัติแล้วนี้?')) onReject(item.id, item.userId); }}
                            className="px-2.5 py-1 rounded-lg bg-white border border-gray-200 text-gray-500 text-[11px] font-medium hover:border-red-200 hover:text-red-500">
                            ปฏิเสธ
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* transfer form (inline) */}
                {showActions && isXfer && (
                  <form onSubmit={submitTransfer} className="bg-green-50 border border-green-200 rounded-xl p-3 space-y-2.5">
                    <div className="text-[11px] font-semibold text-green-800">บันทึกการโอน</div>
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-1">วันที่โอน (ตามสลิป)</label>
                      <input type="date" required value={xferItem.date}
                        onChange={e => setXferItem({ ...xferItem, date: e.target.value })}
                        max={todayStr}
                        className="w-full bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-[11px] text-gray-900 outline-none focus:border-green-400"/>
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-1">แนบสลิปการโอน (ถ้ามี)</label>
                      <div onClick={() => xferFileRef.current?.click()}
                        className="border-2 border-dashed border-green-200 rounded-lg p-3 text-center cursor-pointer hover:border-green-400 bg-white text-[11px]">
                        {xferItem.file
                          ? <span className="text-green-600 flex items-center justify-center gap-1.5"><FileCheck size={14}/>{xferItem.file.name}</span>
                          : <span className="text-gray-400"><CloudUpload size={18} className="mx-auto mb-1 text-green-300"/>คลิกเพื่ออัปโหลดสลิป</span>}
                      </div>
                      <input ref={xferFileRef} type="file" accept="image/*,.pdf" className="hidden"
                        onChange={e => setXferItem({ ...xferItem, file: e.target.files[0] || null })}/>
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setXferItem(null)}
                        className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-[11px] text-gray-600">ยกเลิก</button>
                      <button type="submit" disabled={xferring}
                        className="flex-1 py-2 bg-green-600 text-white rounded-lg text-[11px] font-semibold disabled:opacity-50">
                        {xferring ? 'กำลังบันทึก...' : 'ยืนยันการโอน'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
